import apiClient from "../../../services/apiClient";
import { staffMappedToAnySite, resolveRecipientRoleIds } from "../../../services/lookupService";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import buildTree from "../../../utils/buildTree";

export function stripGuid(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const inner = value.value ?? value.Value ?? value.guid ?? value.Guid;
    if (inner != null) return stripGuid(inner);
  }
  return String(value).trim().replace(/^\{|\}$/g, "");
}

export function normFolderId(id) {
  const s = stripGuid(id);
  return s ? s.toLowerCase() : "";
}

/** Coerce study document state to an array (guards filter index / object payloads). */
export function asStudyDocumentList(documents) {
  return Array.isArray(documents) ? documents : [];
}

export function normalizeTemplateFolderRow(row) {
  if (!row || typeof row !== "object") return row;
  const folderId = row.FolderId ?? row.FolderID ?? row.folderId ?? row.folderID;
  const folderName = row.FolderName ?? row.folderName ?? "";
  const parentId =
    row.ParentFolderIdRef ??
    row.parentFolderIdRef ??
    row.ParentFolderId ??
    row.parentFolderId ??
    null;
  return {
    ...row,
    FolderId: stripGuid(folderId),
    FolderName: folderName,
    ParentFolderIdRef: parentId ? stripGuid(parentId) : parentId,
  };
}

export function normalizeTemplateFolders(folders) {
  return (folders || []).map(normalizeTemplateFolderRow).filter((f) => f.FolderId);
}

export function normalizeTemplatePlaceholderRow(row) {
  if (!row || typeof row !== "object") return row;
  const requiresSignature =
    row.RequiresSignature === true ||
    row.RequiresSignature === 1 ||
    row.RequiresSignature === "1" ||
    row.requiresSignature === true ||
    row.requiresSignature === 1;
  const allowsNotApplicable =
    row.AllowsNotApplicable !== false &&
    row.AllowsNotApplicable !== 0 &&
    row.AllowsNotApplicable !== "0" &&
    row.allowsNotApplicable !== false &&
    row.allowsNotApplicable !== 0;
  return {
    ...row,
    PlaceholderId: stripGuid(row.PlaceholderId ?? row.placeholderId ?? ""),
    TemplateFolderId: stripGuid(row.TemplateFolderId ?? row.templateFolderId ?? ""),
    TemplateId: stripGuid(row.TemplateId ?? row.templateId ?? ""),
    MilestoneId: stripGuid(row.MilestoneId ?? row.milestoneId ?? ""),
    MilestoneCode: row.MilestoneCode ?? row.MilestoneCodeRef ?? row.milestoneCode ?? "",
    MilestoneName: row.MilestoneName ?? row.MilestoneNameRef ?? row.milestoneName ?? "",
    PlaceholderName: row.PlaceholderName ?? row.placeholderName ?? "",
    Description: row.Description ?? row.description ?? "",
    IsRequired: row.IsRequired ?? row.isRequired ?? true,
    IsAdHoc: row.IsAdHoc ?? row.isAdHoc ?? false,
    AdHocBatchId: row.AdHocBatchId ?? row.adHocBatchId ?? "",
    SortOrder: row.SortOrder ?? row.sortOrder ?? 0,
    DefaultDueDays: row.DefaultDueDays ?? row.defaultDueDays,
    FolderName: row.FolderName ?? row.folderName ?? "",
    SiteName: row.SiteName ?? row.siteName ?? "",
    SiteIdRef: row.SiteIdRef ?? row.siteIdRef ?? "",
    ResolvedSiteIdRef: row.ResolvedSiteIdRef ?? row.resolvedSiteIdRef ?? "",
    DefaultAssigneeRole: row.DefaultAssigneeRole ?? row.defaultAssigneeRole ?? "",
    SignerRoles: row.SignerRoles ?? row.signerRoles ?? "",
    RequiresSignature: requiresSignature,
    AllowsNotApplicable: allowsNotApplicable,
  };
}

export function placeholderMatchesFolder(placeholder, folderId, folderName) {
  const p = normalizeTemplatePlaceholderRow(placeholder);
  if (folderId && normFolderId(p.TemplateFolderId) === normFolderId(folderId)) {
    return true;
  }
  const selectedName = (folderName || "").trim().toLowerCase();
  const placeholderFolderName = (p.FolderName || "").trim().toLowerCase();
  return Boolean(
    selectedName && placeholderFolderName && selectedName === placeholderFolderName,
  );
}

/** Resolve template folder id against current folder tree (id first, then name). */
export function resolvePlaceholderFolderId(placeholder, folders = []) {
  const normalizedFolders = normalizeTemplateFolders(folders);
  const folderIds = new Set(normalizedFolders.map((f) => normFolderId(f.FolderId)));
  const rawId = stripGuid(placeholder?.TemplateFolderId);
  if (rawId && folderIds.has(normFolderId(rawId))) {
    return (
      normalizedFolders.find((f) => normFolderId(f.FolderId) === normFolderId(rawId))?.FolderId ||
      rawId
    );
  }
  const folderName = String(placeholder?.FolderName || "").trim().toLowerCase();
  if (folderName) {
    const match = normalizedFolders.find(
      (f) => String(f.FolderName || "").trim().toLowerCase() === folderName,
    );
    if (match?.FolderId) return match.FolderId;
  }
  return rawId || "";
}

export function placeholderSameIdentity(a, b) {
  if (!a || !b) return false;
  if (a._localKey && b._localKey && a._localKey === b._localKey) return true;
  const left = normalizeTemplatePlaceholderRow(a);
  const right = normalizeTemplatePlaceholderRow(b);
  if (left.PlaceholderId && right.PlaceholderId && left.PlaceholderId === right.PlaceholderId) {
    return true;
  }
  return false;
}

export function dedupeTemplatePlaceholders(placeholders) {
  const seen = new Set();
  const result = [];

  (placeholders || []).forEach((raw, index) => {
    const p = normalizeTemplatePlaceholderRow(raw);
    const identityKey = p.PlaceholderId
      ? `id:${p.PlaceholderId.toLowerCase()}`
      : `row:${normFolderId(p.TemplateFolderId)}:${(p.PlaceholderName || "")
          .trim()
          .toLowerCase()}`;

    if (seen.has(identityKey)) return;
    seen.add(identityKey);

    result.push({
      ...p,
      ...raw,
      _localKey:
        raw._localKey ||
        (p.PlaceholderId ? `tpl-ph-${p.PlaceholderId}` : `tpl-ph-dedupe-${index}`),
    });
  });

  return result;
}

export function alignPlaceholdersToFolders(placeholders, folders) {
  const normalizedFolders = normalizeTemplateFolders(folders);
  return (placeholders || []).map((raw) => {
    const p = normalizeTemplatePlaceholderRow(raw);
    const folderId = resolvePlaceholderFolderId(p, normalizedFolders);
    return {
      ...p,
      TemplateFolderId: folderId || p.TemplateFolderId,
    };
  });
}

function normalizeStudyGateCode(code) {
  return String(code || "PRE_STUDY")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function normalizeStudyPlaceholderRow(row) {
  if (!row || typeof row !== "object") return row;
  return {
    ...normalizeTemplatePlaceholderRow(row),
    StudyPlaceholderId: stripGuid(
      row.StudyPlaceholderId ?? row.studyPlaceholderId ?? "",
    ),
    FolderIdRef: stripGuid(row.FolderIdRef ?? row.folderIdRef ?? ""),
    GateCode: row.GateCode ?? row.gateCode ?? "",
  };
}

const ALL_STUDY_GATE_CODES = [
  "PRE_STUDY",
  "START_UP",
  "CONDUCT",
  "CLOSE_OUT",
  "POST_STUDY",
];

function filterStudyPlaceholdersByGate(rows, gateCode) {
  const gate = normalizeStudyGateCode(gateCode);
  return filterStudyPlaceholdersByGates(rows, [gate]);
}

export function filterStudyPlaceholdersByGates(rows, gateCodes = []) {
  const gates = (gateCodes || [])
    .map((code) => normalizeStudyGateCode(code))
    .filter(Boolean);
  if (!gates.length) return rows || [];

  const gateSet = new Set(gates);
  return (rows || []).filter((row) => {
    const status = String(row?.Status || row?.status || "");
    if (status === "Fulfilled" || status === "Waived") return true;
    const rowGate = normalizeStudyGateCode(row?.GateCode || row?.gateCode || "PRE_STUDY");
    return gateSet.has(rowGate);
  });
}

export { filterStudyPlaceholdersByGate };

function studyPlaceholderIdKey(row) {
  return String(row?.StudyPlaceholderId ?? row?.studyPlaceholderId ?? "")
    .trim()
    .toLowerCase();
}

function readinessDetailRowKey(row) {
  return [
    String(row?.SiteIdRef ?? row?.siteIdRef ?? "").trim().toLowerCase(),
    String(row?.FolderName ?? row?.folderName ?? "").trim().toLowerCase(),
    String(row?.PlaceholderName ?? row?.placeholderName ?? "").trim().toLowerCase(),
    String(row?.MilestoneCode ?? row?.milestoneCode ?? "").trim().toLowerCase(),
  ].join("|");
}

function isSignatureRequiredRow(row) {
  return (
    row?.RequiresSignature === 1 ||
    row?.RequiresSignature === true ||
    row?.RequiresSignature === "1" ||
    row?.SignatureState === "signed" ||
    row?.SignatureState === "await"
  );
}

function isSignatureCompleteRow(row) {
  return (
    row?.IsSigned === 1 ||
    row?.IsSigned === true ||
    row?.IsSigned === "1" ||
    row?.SignatureState === "signed"
  );
}

function bumpSignatureRollup(bucket, row) {
  if (!isSignatureRequiredRow(row)) return;
  bucket.SignatureRequired = (bucket.SignatureRequired || 0) + 1;
  if (isSignatureCompleteRow(row)) {
    bucket.SignedCount = (bucket.SignedCount || 0) + 1;
  } else {
    bucket.SignaturePending = (bucket.SignaturePending || 0) + 1;
  }
}

function summarizePlaceholderDetails(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let requiredTotal = 0;
  let requiredFulfilled = 0;
  let requiredPending = 0;
  let overdueCount = 0;
  let dueWithin7Days = 0;
  let dueWithin14Days = 0;
  let dueWithin30Days = 0;
  let signatureRequired = 0;
  let signedCount = 0;
  let signaturePending = 0;

  list.forEach((row) => {
    const isRequired = row?.IsRequired === 1 || row?.IsRequired === true;
    const status = String(row?.Status ?? row?.status ?? "").trim();
    if (isRequired) {
      requiredTotal += 1;
      if (status === "Fulfilled") requiredFulfilled += 1;
      if (status === "Pending" || status === "Overdue") requiredPending += 1;
      if (status === "Overdue") overdueCount += 1;

      const dueRaw = row?.DueDate ?? row?.dueDate;
      if (dueRaw && (status === "Pending" || status === "Overdue")) {
        const dueMs = new Date(dueRaw).setHours(0, 0, 0, 0);
        if (!Number.isNaN(dueMs)) {
          const diffDays = Math.round((dueMs - today.getTime()) / 86400000);
          if (diffDays >= 0 && diffDays <= 7) dueWithin7Days += 1;
          else if (diffDays > 7 && diffDays <= 14) dueWithin14Days += 1;
          else if (diffDays > 14 && diffDays <= 30) dueWithin30Days += 1;
        }
      }
    }
    if (isSignatureRequiredRow(row)) {
      signatureRequired += 1;
      if (isSignatureCompleteRow(row)) signedCount += 1;
      else signaturePending += 1;
    }
  });

  return {
    TotalPlaceholders: list.length,
    RequiredTotal: requiredTotal,
    RequiredFulfilled: requiredFulfilled,
    RequiredPending: requiredPending,
    OverdueCount: overdueCount,
    DueWithin7Days: dueWithin7Days,
    DueWithin14Days: dueWithin14Days,
    DueWithin30Days: dueWithin30Days,
    SignatureRequired: signatureRequired,
    SignedCount: signedCount,
    SignaturePending: signaturePending,
    ReadinessPercent: requiredTotal
      ? Math.round((1000 * requiredFulfilled) / requiredTotal) / 10
      : 0,
  };
}

function computeSiteComparisonFromDetails(rows = []) {
  const bySite = new Map();
  (rows || []).forEach((row) => {
    const siteId = String(row?.SiteIdRef ?? row?.siteIdRef ?? "").trim();
    if (!siteId) return;
    const siteName = row?.SiteName ?? row?.siteName ?? "(No site)";
    const key = siteId.toLowerCase();
    if (!bySite.has(key)) {
      bySite.set(key, {
        SiteIdRef: siteId,
        SiteName: siteName,
        TotalPlaceholders: 0,
        RequiredTotal: 0,
        RequiredFulfilled: 0,
        RequiredPending: 0,
        OverdueCount: 0,
        SignatureRequired: 0,
        SignedCount: 0,
        SignaturePending: 0,
      });
    }
    const bucket = bySite.get(key);
    bucket.TotalPlaceholders += 1;
    bumpSignatureRollup(bucket, row);
    const isRequired = row?.IsRequired === 1 || row?.IsRequired === true;
    const status = String(row?.Status ?? row?.status ?? "").trim();
    if (!isRequired) return;
    bucket.RequiredTotal += 1;
    if (status === "Fulfilled") bucket.RequiredFulfilled += 1;
    if (status === "Pending" || status === "Overdue") bucket.RequiredPending += 1;
    if (status === "Overdue") bucket.OverdueCount += 1;
  });

  return Array.from(bySite.values())
    .map((site) => ({
      ...site,
      ReadinessPercent: site.RequiredTotal
        ? Math.round((1000 * site.RequiredFulfilled) / site.RequiredTotal) / 10
        : 0,
    }))
    .sort((a, b) => String(a.SiteName).localeCompare(String(b.SiteName)));
}

function computeMilestoneSummaryFromDetails(rows = []) {
  const byCode = new Map();
  (rows || []).forEach((row) => {
    const code =
      String(row?.MilestoneCode ?? row?.milestoneCode ?? "").trim() || "(Unassigned)";
    if (!byCode.has(code)) {
      byCode.set(code, {
        MilestoneCode: code,
        MilestoneName:
          row?.MilestoneName ??
          row?.milestoneName ??
          row?.PlaceholderName ??
          row?.placeholderName ??
          code,
        TotalPlaceholders: 0,
        RequiredTotal: 0,
        RequiredFulfilled: 0,
        RequiredPending: 0,
        OverdueCount: 0,
        SignatureRequired: 0,
        SignedCount: 0,
        SignaturePending: 0,
      });
    }
    const bucket = byCode.get(code);
    bucket.TotalPlaceholders += 1;
    bumpSignatureRollup(bucket, row);
    const isRequired = row?.IsRequired === 1 || row?.IsRequired === true;
    const status = String(row?.Status ?? row?.status ?? "").trim();
    if (!isRequired) return;
    bucket.RequiredTotal += 1;
    if (status === "Fulfilled") bucket.RequiredFulfilled += 1;
    if (status === "Pending" || status === "Overdue") bucket.RequiredPending += 1;
    if (status === "Overdue") bucket.OverdueCount += 1;
  });

  return Array.from(byCode.values())
    .map((row) => ({
      ...row,
      ReadinessPercent: row.RequiredTotal
        ? Math.round((1000 * row.RequiredFulfilled) / row.RequiredTotal) / 10
        : 0,
    }))
    .sort((a, b) => String(a.MilestoneCode).localeCompare(String(b.MilestoneCode)));
}

/** Scope readiness report rows to placeholders visible for the selected study phase. */
export function filterReadinessReportByGate(report, gatePlaceholders = []) {
  if (!report) return report;
  const allowedIds = new Set(
    (gatePlaceholders || []).map(studyPlaceholderIdKey).filter(Boolean),
  );
  if (!allowedIds.size) {
    return {
      ...report,
      summary: summarizePlaceholderDetails([]),
      placeholderDetails: [],
      atRiskDetail: [],
      milestoneSummary: [],
      siteComparison: [],
    };
  }

  const placeholderDetails = (report.placeholderDetails || []).filter((row) => {
    const id = studyPlaceholderIdKey(row);
    return id && allowedIds.has(id);
  });
  const detailKeys = new Set(placeholderDetails.map(readinessDetailRowKey));
  const atRiskDetail = (report.atRiskDetail || []).filter((row) =>
    detailKeys.has(readinessDetailRowKey(row)),
  );

  return {
    ...report,
    summary: summarizePlaceholderDetails(placeholderDetails),
    placeholderDetails,
    atRiskDetail,
    milestoneSummary: computeMilestoneSummaryFromDetails(placeholderDetails),
    siteComparison: computeSiteComparisonFromDetails(placeholderDetails),
  };
}

function rowHasColumns(row, requiredColumns = []) {
  if (!row || typeof row !== "object") return false;
  const keys = new Set(
    Object.keys(row).map((key) => String(key).trim().toLowerCase()),
  );
  return requiredColumns.every((col) => keys.has(String(col).trim().toLowerCase()));
}

function findRowsByColumns(data, requiredColumns = []) {
  if (!data || !requiredColumns.length) return null;

  const tables = data.Tables || data.tables;
  if (Array.isArray(tables)) {
    for (const table of tables) {
      const rows = table?.Rows || table?.rows || [];
      if (!rows.length) continue;
      if (rowHasColumns(rows[0], requiredColumns)) {
        return rows;
      }
    }
  }

  for (const key of Object.keys(data)) {
    if (key === "Tables" || key === "tables") continue;
    const value = data[key];
    if (!Array.isArray(value) || !value.length) continue;
    if (rowHasColumns(value[0], requiredColumns)) {
      return value;
    }
  }

  return null;
}

function extractDataTableRows(response, tableName) {
  const data = response?.data;
  if (!data) return [];

  const signature =
    tableName === "StudyPlaceholders"
      ? ["StudyPlaceholderId", "FolderIdRef"]
      : tableName === "StudyMilestones"
        ? ["StudyMilestoneId", "MilestoneCode"]
        : null;

  const normalizeRow =
    tableName === "StudyPlaceholders"
      ? normalizeStudyPlaceholderRow
      : normalizeTemplatePlaceholderRow;

  if (signature) {
    const matched = findRowsByColumns(data, signature);
    if (matched?.length) {
      return matched.map(normalizeRow);
    }
  }

  const tryArray = (value) =>
    Array.isArray(value) ? value.map(normalizeRow) : null;

  const direct =
    tryArray(data[tableName]) ??
    tryArray(data[tableName?.toLowerCase?.()]) ??
    tryArray(data[tableName?.toUpperCase?.()]) ??
    tryArray(data.Table) ??
    tryArray(data.table);
  if (direct?.length) return direct;

  if (Array.isArray(data)) return data.map(normalizeRow);

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (key === "Tables") continue;
      const rows = tryArray(data[key]);
      if (rows?.length) return rows;
    }

    if (Array.isArray(data?.Tables)) {
      const table = data.Tables.find(
        (t) =>
          t?.TableName === tableName ||
          t?.Name === tableName ||
          t?.tableName === tableName,
      );
      if (Array.isArray(table?.Rows)) {
        return table.Rows.map(normalizeRow);
      }
      const first = data.Tables.find((t) => Array.isArray(t?.Rows) && t.Rows.length);
      if (first?.Rows) return first.Rows.map(normalizeRow);
    }
  }

  return [];
}

export async function fetchTemplatePlaceholders({ organizationId, templateId, createdBy }) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetTemplatePlaceholders",
    OrganizationIdRef: organizationId,
    TemplateId: templateId,
    CreatedBy: createdBy,
  });

  let rows = extractDataTableRows(response, "TemplatePlaceholders");
  if (!rows.length && response?.data) {
    const fallback =
      response.data.TemplatePlaceholders ??
      response.data.templatePlaceholders ??
      response.data.Table ??
      response.data.table;
    if (Array.isArray(fallback)) {
      rows = fallback.map(normalizeTemplatePlaceholderRow);
    } else if (Array.isArray(response.data)) {
      rows = response.data.map(normalizeTemplatePlaceholderRow);
    }
  }

  return rows;
}

export function buildTemplatePlaceholderPayload(rows) {
  return (rows || []).map((r, i) => ({
    PlaceholderId: r.PlaceholderId || "",
    TemplateFolderId: r.TemplateFolderId,
    PlaceholderName: r.PlaceholderName,
    Description: r.Description || "",
    IsRequired: r.IsRequired !== false && r.IsRequired !== 0,
    SortOrder: r.SortOrder ?? i,
    DefaultDueDays:
      r.DefaultDueDays === "" || r.DefaultDueDays == null ? null : Number(r.DefaultDueDays),
    DefaultAssigneeRole: r.DefaultAssigneeRole || "",
    RequiresSignature:
      r.RequiresSignature === true || r.RequiresSignature === 1 || r.RequiresSignature === "1",
    SignerRoles: r.SignerRoles || "",
    AllowsNotApplicable:
      r.AllowsNotApplicable === true ||
      r.AllowsNotApplicable === 1 ||
      r.AllowsNotApplicable === "1",
    MilestoneId: stripGuid(r.MilestoneId) || "",
  }));
}

export function getPlanMilestoneIdSet(planMappingPreview = []) {
  return new Set(
    (planMappingPreview || [])
      .map((row) => stripGuid(row.MilestoneId)?.toLowerCase())
      .filter(Boolean),
  );
}

export function inferManualMilestoneAssignment(
  placeholder,
  planMappingPreview = [],
  folders = [],
) {
  const milestoneId = stripGuid(placeholder?.MilestoneId)?.toLowerCase();
  if (!milestoneId) return true;
  if (placeholder?._manualMilestone) return true;

  const planRow = (planMappingPreview || []).find(
    (row) => stripGuid(row.MilestoneId)?.toLowerCase() === milestoneId,
  );
  if (!planRow) return true;

  const expectedFolderName = String(planRow.DefaultFolderName || "").trim().toLowerCase();
  const assignedFolder = (folders || []).find(
    (f) => normFolderId(f.FolderId) === normFolderId(placeholder.TemplateFolderId),
  );
  if (!assignedFolder) return true;

  return String(assignedFolder.FolderName || "").trim().toLowerCase() !== expectedFolderName;
}

export function isManualTemplatePlaceholder(
  placeholder,
  planMilestoneIds,
  planMappingPreview = [],
  folders = [],
) {
  if (!stripGuid(placeholder?.MilestoneId)) return true;
  if (placeholder?._manualMilestone) return true;
  return inferManualMilestoneAssignment(placeholder, planMappingPreview, folders);
}

export function filterManualTemplatePlaceholders(
  placeholders,
  planMilestoneIds,
  planMappingPreview = [],
  folders = [],
) {
  return (placeholders || []).filter((p) =>
    isManualTemplatePlaceholder(p, planMilestoneIds, planMappingPreview, folders),
  );
}

/** Rows that must be persisted via MergeManual (UI-assigned, unsaved, or non-default folder). */
export function getManualAssignmentsToPersist(
  placeholders,
  planMappingPreview = [],
  folders = [],
) {
  const seen = new Set();
  const rows = [];
  (placeholders || []).forEach((raw) => {
    const p = normalizeTemplatePlaceholderRow(raw);
    const milestoneId = stripGuid(p.MilestoneId);
    if (!milestoneId) return;

    const isManual =
      raw._manualMilestone ||
      !stripGuid(p.PlaceholderId) ||
      inferManualMilestoneAssignment(p, planMappingPreview, folders);
    if (!isManual) return;

    const folderId = resolvePlaceholderFolderId(p, folders);
    if (!folderId) return;

    const key = `${milestoneId.toLowerCase()}:${normFolderId(folderId)}:${(p.PlaceholderName || "")
      .trim()
      .toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      ...p,
      ...raw,
      TemplateFolderId: folderId,
    });
  });
  return rows;
}

/** Placeholders that must be persisted via MergeManual (unsaved rows + manual folder assignments). */
export function collectPlaceholdersForMerge(
  placeholders,
  planMilestoneIds,
  planMappingPreview = [],
  folders = [],
) {
  return getManualAssignmentsToPersist(placeholders, planMappingPreview, folders);
}

export function getTemplateUsedMilestoneIds(
  placeholders = [],
  planMappingPreview = [],
  { includePlanPreview = true } = {},
) {
  const used = new Set();
  (placeholders || []).forEach((p) => {
    const id = stripGuid(p.MilestoneId)?.toLowerCase();
    if (id) used.add(id);
  });
  if (includePlanPreview) {
    (planMappingPreview || []).forEach((row) => {
      const id = stripGuid(row.MilestoneId)?.toLowerCase();
      if (id) used.add(id);
    });
  }
  return used;
}

/** Milestone ids already on this template (saved placeholders only). */
export function getAssignedMilestoneIds(placeholders = []) {
  return getTemplateUsedMilestoneIds(placeholders, [], { includePlanPreview: false });
}

export async function saveTemplatePlaceholders({ organizationId, templateId, createdBy, placeholders }) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "SaveTemplatePlaceholders",
    OrganizationIdRef: organizationId,
    TemplateId: templateId,
    CreatedBy: createdBy,
    Placeholders: buildTemplatePlaceholderPayload(placeholders),
  });
}

export async function mergeManualTemplatePlaceholders({
  organizationId,
  templateId,
  createdBy,
  placeholders,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "MergeManualTemplatePlaceholders",
    OrganizationIdRef: organizationId,
    TemplateId: templateId,
    CreatedBy: createdBy,
    Placeholders: buildTemplatePlaceholderPayload(placeholders),
  });
}

export async function removeTemplatePlaceholder({
  organizationId,
  templateId,
  placeholderId,
  milestoneId,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "RemoveTemplatePlaceholder",
    OrganizationIdRef: organizationId,
    TemplateId: templateId,
    StudyPlaceholderId: placeholderId || null,
    MilestoneId: milestoneId || null,
    CreatedBy: createdBy,
  });
}

export async function fetchStudyPlaceholders({
  organizationId,
  studyId,
  folderId,
  templateCategory,
  siteIdRef,
  createdBy,
  gateCode,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetStudyPlaceholders",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    FolderIdRef: folderId || null,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    CreatedBy: createdBy,
    GateCode: gateCode || null,
  });
  let rows = extractDataTableRows(response, "StudyPlaceholders");
  if (!rows.length && response?.data) {
    const fallback = response.data.StudyPlaceholders ?? response.data.studyPlaceholders;
    if (Array.isArray(fallback)) rows = fallback;
  }
  return rows;
}

/** Load placeholders for one or more study phases; empty gateCodes fetches all phases. */
export async function fetchStudyPlaceholdersForGates({
  organizationId,
  studyId,
  folderId,
  templateCategory,
  siteIdRef,
  createdBy,
  gateCodes = [],
}) {
  const normalizedCodes = (gateCodes || [])
    .map((code) => normalizeStudyGateCode(code))
    .filter(Boolean);
  const fetchCodes = normalizedCodes.length ? normalizedCodes : ALL_STUDY_GATE_CODES;

  const batches = await Promise.all(
    fetchCodes.map((gateCode) =>
      fetchStudyPlaceholders({
        organizationId,
        studyId,
        folderId,
        templateCategory,
        siteIdRef,
        createdBy,
        gateCode,
      }).catch(() => []),
    ),
  );

  const merged = new Map();
  batches.flat().forEach((row) => {
    const key = studyPlaceholderIdKey(row);
    if (key) merged.set(key, row);
  });
  return Array.from(merged.values());
}

/** Site view: site-specific rows win over study-wide for the same milestone slot. */
export function mergeStudyPlaceholdersForSite(siteRows = [], studyWideRows = []) {
  const merged = new Map();
  (studyWideRows || []).forEach((row) => {
    merged.set(placeholderTreeSlotKey(row), row);
  });
  (siteRows || []).forEach((row) => {
    merged.set(placeholderTreeSlotKey(row), row);
  });
  return Array.from(merged.values());
}

/**
 * Load placeholders for the active site only.
 * Multi-site binders must not mix another site's fulfilled slots into this tree.
 */
export async function fetchStudyPlaceholdersForActiveSite({
  organizationId,
  studyId,
  templateCategory,
  siteIdRef,
  createdBy,
  gateCode = "PRE_STUDY",
}) {
  const common = {
    organizationId,
    studyId,
    templateCategory,
    createdBy,
    gateCode,
  };
  if (!siteIdRef) {
    return fetchStudyPlaceholders({ ...common, siteIdRef: null });
  }
  const siteSpecific = await fetchStudyPlaceholders({ ...common, siteIdRef });
  return (siteSpecific || []).filter((row) =>
    studyPlaceholderMatchesSite(row, siteIdRef),
  );
}

/** When placeholder FolderIdRef predates site folder copy, resolve by folder name + site. */
export function alignStudyPlaceholdersToFolders(placeholders = [], folders = []) {
  const byId = new Map();
  const byNameAndSite = new Map();
  (folders || []).forEach((folder) => {
    const id = normFolderId(folder.id ?? folder.FolderId);
    if (!id) return;
    byId.set(id, id);
    const name = String(folder.name ?? folder.FolderName ?? "")
      .trim()
      .toLowerCase();
    const site = normFolderId(folder.SiteIdRef ?? folder.siteIdRef);
    if (name) byNameAndSite.set(`${name}|${site}`, id);
  });

  return (placeholders || []).map((row) => {
    const current = normFolderId(row.FolderIdRef);
    if (current && byId.has(current)) return row;
    const name = String(row.FolderName ?? row.folderName ?? "")
      .trim()
      .toLowerCase();
    if (!name) return row;
    const rowSite = normFolderId(row.SiteIdRef ?? row.siteIdRef);
    const resolved = byNameAndSite.get(`${name}|${rowSite}`);
    return resolved ? { ...row, FolderIdRef: resolved } : row;
  });
}

export async function ensureStudyPlaceholdersCloned({
  organizationId,
  studyId,
  templateId,
  templateCategory,
  siteIdRef,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "EnsureStudyPlaceholdersCloned",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateId: templateId || null,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    CreatedBy: createdBy,
  });
}

/** Stage 2.3 — resolve uploader role(s) to site staff rows for My Tasks. */
export async function seedStudyPlaceholderAssignees({
  organizationId,
  studyId,
  templateCategory,
  siteIdRef,
  createdBy,
  studyPlaceholderId = null,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "SeedStudyPlaceholderAssignees",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    StudyPlaceholderId: studyPlaceholderId || null,
    CreatedBy: createdBy,
  });
}

export async function fetchStudySourceTemplate({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
  siteIdRef = null,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetStudySourceTemplate",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
    SiteIdRef: siteIdRef || null,
  });
  let rows = extractDataTableRows(response, "StudySourceTemplate");
  if (!rows.length && response?.data) {
    const fallback = response.data.StudySourceTemplate ?? response.data.studySourceTemplate;
    if (Array.isArray(fallback)) rows = fallback;
  }
  const row = rows[0];
  if (!row?.TemplateId) return null;
  return {
    TemplateId: stripGuid(row.TemplateId),
    TemplateName: row.TemplateName ?? row.templateName ?? "",
  };
}

function extractReportTableRows(response, tableName) {
  const data = response?.data ?? response;
  if (!data) return [];

  const target = String(tableName || "").toLowerCase();
  const tryArray = (value) => (Array.isArray(value) ? value : null);

  const direct =
    tryArray(data[tableName]) ??
    tryArray(data[tableName?.toLowerCase?.()]) ??
    tryArray(data[tableName?.toUpperCase?.()]);
  if (direct) return direct;

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (key === "Tables") continue;
      if (String(key).toLowerCase() === target) {
        const rows = tryArray(data[key]);
        if (rows) return rows;
      }
    }
  }

  if (Array.isArray(data?.Tables)) {
    const table = data.Tables.find(
      (t) =>
        String(t?.TableName || t?.Name || t?.tableName || "").toLowerCase() === target,
    );
    if (Array.isArray(table?.Rows)) return table.Rows;
  }

  return [];
}

export async function fetchReadinessReport({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
  siteIdRef = null,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetReadinessReport",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
    SiteIdRef: siteIdRef || null,
  });
  return {
    summary: extractReportTableRows(response, "Summary")[0] ?? null,
    placeholderDetails: extractReportTableRows(response, "PlaceholderDetails"),
    milestoneSummary: extractReportTableRows(response, "MilestoneSummary"),
    folderCompleteness: extractReportTableRows(response, "FolderCompleteness"),
    atRiskDetail: extractReportTableRows(response, "AtRiskDetail"),
    siteComparison: extractReportTableRows(response, "SiteComparison"),
  };
}

export async function applyPlaceholdersToStudy({
  organizationId,
  studyId,
  templateId,
  templateCategory,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "ApplyTemplatePlaceholdersToStudy",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateId: templateId,
    TemplateCategory: templateCategory,
    CreatedBy: createdBy,
  });
}

export async function linkStudyPlaceholderDocument({
  organizationId,
  studyId,
  studyPlaceholderId,
  documentIdRef,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "LinkDocumentToPlaceholder",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    StudyPlaceholderId: studyPlaceholderId,
    DocumentIdRef: documentIdRef,
    CreatedBy: createdBy,
  });
}

/** Find uploaded file for a study placeholder (forward + reverse link). */
export function findDocumentForStudyPlaceholder(
  placeholder,
  documents = [],
  placeholders = [],
) {
  if (!placeholder) return null;
  const docList = asStudyDocumentList(documents);
  const linked = resolveMilestoneLinkedDocument(placeholder, docList);
  if (linked) return linked;

  const phIds = collectPlaceholderLookupIds(placeholder, placeholders);
  for (const phId of phIds) {
    const byPhId = docList.find((doc) => {
      if (!isActiveStudyDocument(doc)) return false;
      return (
        stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId).toLowerCase() ===
        phId
      );
    });
    if (byPhId) return byPhId;
  }

  const docId = stripGuid(
    placeholder.DocumentIdRef ?? placeholder.documentIdRef,
  ).toLowerCase();
  if (docId) {
    const byDocId = docList.find(
      (doc) =>
        isActiveStudyDocument(doc) &&
        stripGuid(doc.DocumentIdRef).toLowerCase() === docId,
    );
    if (byDocId) return byDocId;
  }

  const fulfilledName = String(
    placeholder.FulfilledDocumentName ?? placeholder.fulfilledDocumentName ?? "",
  )
    .trim()
    .toLowerCase();
  if (fulfilledName) {
    const byName = docList.find(
      (doc) =>
        isActiveStudyDocument(doc) &&
        String(doc.FileName || "").trim().toLowerCase() === fulfilledName,
    );
    if (byName) return byName;
  }

  if (String(placeholder.Status || placeholder.placeholderStatus || "").toLowerCase() === "fulfilled") {
    const phPool = placeholders.length ? placeholders : [placeholder];
    const parentMap = buildDocumentPlaceholderParentMap(phPool, documents);
    for (const phId of phIds) {
      const phTreeId = `ph-${phId}`;
      const matched = (documents || []).find((doc) => {
        if (!isActiveStudyDocument(doc)) return false;
        return (
          parentMap.get(stripGuid(doc.DocumentIdRef).toLowerCase()) === phTreeId
        );
      });
      if (matched) return matched;
    }
  }

  return null;
}

/** Unlinked files in the same folder as the placeholder (for auto-repair). */
export function findUnlinkedFolderDocuments(placeholder, documents = [], placeholders = []) {
  if (!placeholder) return [];
  const folderId = stripGuid(placeholder.FolderIdRef).toLowerCase();

  const isUnlinked = (doc) => {
    if (!isActiveStudyDocument(doc)) return false;
    if (stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId)) return false;
    const usedByPh = (placeholders || []).some(
      (p) =>
        stripGuid(p.DocumentIdRef).toLowerCase() ===
        stripGuid(doc.DocumentIdRef).toLowerCase(),
    );
    return !usedByPh;
  };

  const allUnlinked = (documents || []).filter(isUnlinked);
  if (!folderId) return allUnlinked;

  const inFolder = allUnlinked.filter(
    (doc) => stripGuid(doc.FolderIdRef).toLowerCase() === folderId,
  );
  return inFolder.length ? inFolder : allUnlinked;
}

/** Files in the same folder that can be linked to this placeholder (manual repair UI). */
export function findLinkableFolderDocuments(placeholder, documents = []) {
  if (!placeholder) return [];
  const folderId = stripGuid(placeholder.FolderIdRef).toLowerCase();
  const phId = stripGuid(
    placeholder.StudyPlaceholderId ?? placeholder.studyPlaceholderId,
  ).toLowerCase();

  return (documents || []).filter((doc) => {
    if (!isActiveStudyDocument(doc)) return false;
    if (folderId && stripGuid(doc.FolderIdRef).toLowerCase() !== folderId) {
      return false;
    }
    const docPhId = stripGuid(
      doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
    ).toLowerCase();
    if (docPhId && phId && docPhId === phId) return false;
    return true;
  });
}

/** Resolve the saved document id after upload (SP may return id under different fields). */
export function resolveUploadedStudyDocumentId(
  responseRow,
  folderDocuments = [],
  { fileName, folderId, fallbackId } = {},
) {
  const candidateIds = [
    responseRow?.DocumentIdRef,
    responseRow?.documentIdRef,
    responseRow?.FolderIdRef,
    responseRow?.folderIdRef,
    fallbackId,
  ]
    .map((id) => stripGuid(id))
    .filter(Boolean);

  for (const id of candidateIds) {
    const norm = id.toLowerCase();
    const match = (folderDocuments || []).find(
      (d) => stripGuid(d.DocumentIdRef).toLowerCase() === norm,
    );
    if (match?.DocumentIdRef) return match.DocumentIdRef;
  }

  const folderNorm = stripGuid(folderId).toLowerCase();
  const nameNorm = String(fileName || "").trim().toLowerCase();
  if (nameNorm) {
    const matches = (folderDocuments || []).filter((d) => {
      if (!isActiveStudyDocument(d)) return false;
      if (
        folderNorm &&
        stripGuid(d.FolderIdRef).toLowerCase() !== folderNorm
      ) {
        return false;
      }
      return String(d.FileName || "").trim().toLowerCase() === nameNorm;
    });
    if (matches.length === 1) return matches[0].DocumentIdRef;
    if (matches.length > 1) {
      matches.sort((a, b) => {
        const ta = new Date(a.SystemDateTime || 0).getTime();
        const tb = new Date(b.SystemDateTime || 0).getTime();
        return tb - ta;
      });
      return matches[0].DocumentIdRef;
    }
  }

  return candidateIds[0] || fallbackId || "";
}

/** Resolve folder display name from the study tree (for upload payload FolderName). */
export function findStudyFolderNameInTree(treeNodes = [], folderId) {
  const folderNorm = stripGuid(folderId).toLowerCase();
  if (!folderNorm) return "";

  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (
        node?.type === "folder" &&
        stripGuid(node.id).toLowerCase() === folderNorm
      ) {
        return String(node.name || "").trim();
      }
      if (node?.children?.length) {
        const nested = walk(node.children);
        if (nested) return nested;
      }
    }
    return "";
  };

  return walk(treeNodes);
}

/** Active document in a version group — highest VersionNo wins. */
export function resolveActiveDocumentInFileGroup(
  documents = [],
  fileGroupIdRef,
  { preferDocumentId } = {},
) {
  const groupNorm = stripGuid(fileGroupIdRef).toLowerCase();
  if (!groupNorm) return null;

  const active = (documents || []).filter(
    (d) =>
      isActiveStudyDocument(d) &&
      stripGuid(d.FileGroupIdRef).toLowerCase() === groupNorm,
  );
  if (!active.length) return null;

  if (preferDocumentId) {
    const preferNorm = stripGuid(preferDocumentId).toLowerCase();
    const direct = active.find(
      (d) => stripGuid(d.DocumentIdRef).toLowerCase() === preferNorm,
    );
    if (direct) return direct;
  }

  active.sort((a, b) => {
    const va = parseFloat(a.VersionNo) || 0;
    const vb = parseFloat(b.VersionNo) || 0;
    if (vb !== va) return vb - va;
    const ta = new Date(a.SystemDateTime || 0).getTime();
    const tb = new Date(b.SystemDateTime || 0).getTime();
    return tb - ta;
  });

  return active[0];
}

/** After supersede upload, pick the new active version in the same file group. */
export function resolveDocumentIdAfterSupersedeUpload(
  responseRow,
  folderDocuments = [],
  supersededDocument,
  { fileName, folderId, fallbackId } = {},
) {
  const fromResponse =
    responseRow?.DocumentIdRef ??
    responseRow?.documentIdRef ??
    fallbackId ??
    "";

  const fileGroupId =
    supersededDocument?.FileGroupIdRef ??
    responseRow?.FileGroupIdRef ??
    responseRow?.fileGroupIdRef;

  if (fileGroupId) {
    const activeInGroup = resolveActiveDocumentInFileGroup(
      folderDocuments,
      fileGroupId,
      { preferDocumentId: fromResponse },
    );
    if (activeInGroup?.DocumentIdRef) return activeInGroup.DocumentIdRef;
  }

  return (
    resolveUploadedStudyDocumentId(responseRow, folderDocuments, {
      fileName,
      folderId,
      fallbackId: fromResponse,
    }) || fromResponse
  );
}

export async function syncBrokenPlaceholderDocumentLinks({
  organizationId,
  studyId,
  createdBy,
  placeholders = [],
  documents = [],
}) {
  const repairs = [];
  (placeholders || []).forEach((ph) => {
    if (String(ph.Status || "").toLowerCase() !== "fulfilled") return;
    if (findDocumentForStudyPlaceholder(ph, documents)) return;

    const phId = stripGuid(ph.StudyPlaceholderId).toLowerCase();
    const docByPhId = (documents || []).find((doc) => {
      if (!isActiveStudyDocument(doc)) return false;
      return (
        stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId).toLowerCase() ===
        phId
      );
    });
    const docByStoredId = (documents || []).find((doc) => {
      if (!isActiveStudyDocument(doc)) return false;
      return (
        stripGuid(ph.DocumentIdRef).toLowerCase() ===
        stripGuid(doc.DocumentIdRef).toLowerCase()
      );
    });
    let targetDoc = docByPhId || docByStoredId;
    if (!targetDoc) {
      const fulfilledName = String(ph.FulfilledDocumentName || "")
        .trim()
        .toLowerCase();
      if (fulfilledName) {
        targetDoc = (documents || []).find(
          (doc) =>
            isActiveStudyDocument(doc) &&
            String(doc.FileName || "").trim().toLowerCase() === fulfilledName,
        );
      }
    }
    if (!targetDoc) {
      const orphans = findUnlinkedFolderDocuments(ph, documents, placeholders);
      if (orphans.length === 1) targetDoc = orphans[0];
    }
    if (!targetDoc?.DocumentIdRef) return;

    repairs.push(
      linkStudyPlaceholderDocument({
        organizationId,
        studyId,
        studyPlaceholderId: ph.StudyPlaceholderId,
        documentIdRef: targetDoc.DocumentIdRef,
        createdBy,
      }),
    );
  });

  if (!repairs.length) return false;
  await Promise.all(repairs);
  return true;
}

export async function waiveStudyPlaceholder({
  organizationId,
  studyId,
  studyPlaceholderId,
  waivedReason,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "WaiveStudyPlaceholder",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    StudyPlaceholderId: studyPlaceholderId,
    WaivedReason: waivedReason,
    CreatedBy: createdBy,
  });
}

/** Only optional, pending placeholders may be waived (not fulfilled / linked). */
export function studyPlaceholderCanBeWaived(
  row,
  documents = null,
  placeholders = [],
) {
  if (!row) return false;
  const isOptional =
    row.IsRequired === false ||
    row.IsRequired === 0 ||
    row.isRequired === false;
  if (!isOptional) return false;

  const docList = Array.isArray(documents) ? documents : null;
  const resolved = docList?.length
    ? reconcileStudyPlaceholderRow(row, docList, placeholders)
    : row;
  const status = String(
    resolved?.Status ?? resolved?.status ?? resolved?.placeholderStatus ?? "",
  ).toLowerCase();
  if (status === "fulfilled" || status === "waived") return false;
  if (stripGuid(resolved?.DocumentIdRef ?? resolved?.documentIdRef)) return false;
  if (
    docList?.length &&
    findDocumentForStudyPlaceholder(resolved, docList, placeholders)
  ) {
    return false;
  }
  return true;
}

function parseCsvIds(value) {
  return String(value || "")
    .split(",")
    .map((part) => normalizeStaffId(part))
    .filter(Boolean);
}

function normalizeStaffId(value) {
  return stripGuid(value).toLowerCase();
}

function parseRoleCandidates(row) {
  const tokens = [];
  [
    row?.AssigneeRole,
    row?.assigneeRole,
    row?.DefaultAssigneeRole,
    row?.defaultAssigneeRole,
    row?.AssigneeRoleName,
    row?.assigneeRoleName,
  ].forEach((value) => {
    String(value || "")
      .split(/[,;|]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => tokens.push(part));
  });
  return tokens;
}

function normalizeRoleName(value) {
  return String(value || "").trim().toLowerCase();
}

function expandAssigneeRoleTokens(tokens = [], roleOptions = []) {
  const roleIds = new Set();
  const roleNames = new Set();

  (tokens || []).forEach((token) => {
    const raw = String(token || "").trim();
    if (!raw) return;

    roleNames.add(normalizeRoleName(raw));

    resolveRecipientRoleIds(raw, roleOptions).forEach((roleId) => {
      const normalizedId = normalizeStaffId(roleId);
      if (!normalizedId) return;
      roleIds.add(normalizedId);
      const match = (roleOptions || []).find(
        (role) => normalizeStaffId(role.roleId || role.value) === normalizedId,
      );
      if (match?.roleName) roleNames.add(normalizeRoleName(match.roleName));
    });

    const byName = (roleOptions || []).find(
      (role) => normalizeRoleName(role.roleName || role.label) === normalizeRoleName(raw),
    );
    if (byName?.roleId) {
      roleIds.add(normalizeStaffId(byName.roleId));
      roleNames.add(normalizeRoleName(byName.roleName || byName.label));
    }
  });

  return { roleIds, roleNames };
}

function resolveUserRoleId(userData, staffList = [], roleOptions = []) {
  const staffId = normalizeStaffId(userData?.StaffId);
  const userStaff = (staffList || []).find(
    (staff) => normalizeStaffId(staff.value || staff.id) === staffId,
  );
  let roleId = normalizeStaffId(
    userData?.RoleId ||
      userData?.roleId ||
      userStaff?.roleId ||
      userStaff?.RoleId,
  );

  if (!roleId) {
    const roleName = normalizeRoleName(userStaff?.Role || userData?.Role);
    if (roleName) {
      const match = (roleOptions || []).find(
        (role) =>
          normalizeRoleName(role.roleName || role.label) === roleName,
      );
      if (match?.roleId) roleId = normalizeStaffId(match.roleId);
    }
  }

  return roleId;
}

function userMatchesAssigneeRole(row, userData, staffList = [], roleOptions = []) {
  const userRoleId = resolveUserRoleId(userData, staffList, roleOptions);
  const { roleIds, roleNames } = expandAssigneeRoleTokens(
    parseRoleCandidates(row),
    roleOptions,
  );

  if (userRoleId && roleIds.has(userRoleId)) return true;

  const userRoleName = normalizeRoleName(userData?.Role);
  if (userRoleName && roleNames.has(userRoleName)) return true;

  return false;
}

function isUserOnStudyStaff(staffId, staffList = []) {
  return (staffList || []).some(
    (staff) => normalizeStaffId(staff.value || staff.id) === staffId,
  );
}

function isSystemAdministrator(userData) {
  return userData?.Role === "System Administrator";
}

function isStudyPlaceholderAdmin(userData, permissions = []) {
  if (!userData) return false;
  if (isSystemAdministrator(userData)) return true;
  return (
    permissions.includes("AdminRole") ||
    permissions.includes("MANAGE_STUDY_PLACEHOLDERS") ||
    permissions.includes("MILESTONE_LIBRARY") ||
    permissions.includes("ADD_MILESTONE") ||
    permissions.includes("EDIT_MILESTONE") ||
    permissions.includes("TEMPLATE_MANAGER") ||
    permissions.includes("UPDATE_TEMPLATE")
  );
}

function hasAdHocPlaceholderPermission(permissions = [], permissionKey, userData) {
  if (isSystemAdministrator(userData)) return true;
  return permissions.includes(permissionKey);
}

export function hasStudyPlaceholderPermission(permissions = [], permissionKey, userData) {
  if (isStudyPlaceholderAdmin(userData, permissions)) return true;
  return permissions.includes(permissionKey);
}

export function canCreateStudyAdHocPlaceholder(permissions = [], userData) {
  return hasAdHocPlaceholderPermission(
    permissions,
    "CREATE_STUDY_ADHOC_PLACEHOLDER",
    userData,
  );
}

export function canEditStudyAdHocPlaceholder(permissions = [], userData) {
  return hasAdHocPlaceholderPermission(
    permissions,
    "EDIT_STUDY_ADHOC_PLACEHOLDER",
    userData,
  );
}

export function canRemoveStudyAdHocPlaceholder(permissions = [], userData) {
  return hasAdHocPlaceholderPermission(
    permissions,
    "REMOVE_STUDY_ADHOC_PLACEHOLDER",
    userData,
  );
}

/** @deprecated Use canRemoveStudyAdHocPlaceholder */
export function canDeleteStudyAdHocPlaceholder(permissions = [], userData) {
  return canRemoveStudyAdHocPlaceholder(permissions, userData);
}

export function canRemoveStudyPlaceholder(permissions = [], userData) {
  return isStudyPlaceholderAdmin(userData, permissions);
}

export function canModifyStudyAdHocRow(row, permissions = [], userData) {
  if (!row) return false;
  const isAdHoc =
    row.IsAdHoc === true ||
    row.IsAdHoc === 1 ||
    row.isAdHoc === true ||
    row.isAdHoc === 1;
  if (!isAdHoc) return false;
  return (
    canCreateStudyAdHocPlaceholder(permissions, userData) ||
    canEditStudyAdHocPlaceholder(permissions, userData) ||
    canRemoveStudyAdHocPlaceholder(permissions, userData)
  );
}

export function canRemoveStudyPlaceholderRow(row, permissions = [], userData) {
  if (!row) return false;
  const isAdHoc =
    row.IsAdHoc === true ||
    row.IsAdHoc === 1 ||
    row.isAdHoc === true ||
    row.isAdHoc === 1;
  if (!isAdHoc) return false;
  return canRemoveStudyAdHocPlaceholder(permissions, userData);
}

/** Align placeholder status with linked documents and tree node metadata. */
export function reconcileStudyPlaceholderRow(
  row,
  documents = [],
  placeholders = [],
) {
  if (!row) return null;

  const docList = asStudyDocumentList(documents);
  const linked = docList.length
    ? findDocumentForStudyPlaceholder(row, docList, placeholders || [])
    : null;
  const status = String(
    row.Status ?? row.status ?? row.placeholderStatus ?? "",
  ).toLowerCase();
  const hasDocLink = Boolean(linked || stripGuid(row.DocumentIdRef ?? row.documentIdRef));

  if (hasDocLink && status !== "fulfilled" && status !== "waived") {
    return {
      ...row,
      Status: "Fulfilled",
      placeholderStatus: "Fulfilled",
      DocumentIdRef: row.DocumentIdRef || linked?.DocumentIdRef || null,
      FulfilledDocumentName:
        row.FulfilledDocumentName || linked?.FileName || null,
    };
  }

  if (linked) {
    return {
      ...row,
      DocumentIdRef: row.DocumentIdRef || linked.DocumentIdRef || null,
      FulfilledDocumentName: row.FulfilledDocumentName || linked.FileName || null,
    };
  }

  const displayStatus =
    row.placeholderStatus || row.Status || row.status || null;
  if (displayStatus && String(row.Status || "").toLowerCase() !== displayStatus.toLowerCase()) {
    return {
      ...row,
      Status: displayStatus,
      placeholderStatus: displayStatus,
    };
  }
  if (row.Status && !row.placeholderStatus) {
    return { ...row, placeholderStatus: row.Status };
  }
  if (row.placeholderStatus && !row.Status) {
    return { ...row, Status: row.placeholderStatus };
  }

  return row;
}

export function getStudyPlaceholderDisplayStatus(
  row,
  documents = [],
  placeholders = [],
) {
  const reconciled = reconcileStudyPlaceholderRow(row, documents, placeholders);
  if (!reconciled) return "Pending";
  if (studyPlaceholderAwaitingMilestoneDates(reconciled)) {
    return "Awaiting milestone dates";
  }
  return reconciled.placeholderStatus || reconciled.Status || "Pending";
}

/** Resolve full placeholder row from study list (tree nodes may omit assignee fields). */
export function resolveStudyPlaceholderRow(
  node,
  studyPlaceholders = [],
  documents = [],
) {
  if (!node) return null;
  const placeholderId = node.studyPlaceholderId || node.StudyPlaceholderId;
  let row = node;
  if (placeholderId) {
    const match = (studyPlaceholders || []).find(
      (r) =>
        String(r.StudyPlaceholderId).toLowerCase() ===
        String(placeholderId).toLowerCase(),
    );
    if (match) row = { ...node, ...match };
  }
  return reconcileStudyPlaceholderRow(row, documents, studyPlaceholders);
}

/** True when the logged-in user may act on this placeholder (upload, etc.). */
export function studyPlaceholderAssignedToUser(
  row,
  userData,
  { staffList = [], roleOptions = [] } = {},
) {
  if (!row || !userData?.StaffId) return false;

  const staffId = normalizeStaffId(userData.StaffId);
  const assignedTo = normalizeStaffId(row.AssignedToStaffId || row.assignedToStaffId);
  if (assignedTo && assignedTo === staffId) return true;

  const eligibleStaffIds = parseCsvIds(row.EligibleStaffIds || row.eligibleStaffIds);
  if (eligibleStaffIds.includes(staffId)) return true;

  if (userMatchesAssigneeRole(row, userData, staffList, roleOptions)) return true;

  const isAdHoc =
    row.IsAdHoc === true ||
    row.IsAdHoc === 1 ||
    row.isAdHoc === true ||
    row.isAdHoc === 1;
  if (isAdHoc) return false;

  const roleTokens = parseRoleCandidates(row);
  const hasExplicitStaffAssignment =
    Boolean(assignedTo) || eligibleStaffIds.length > 0;

  if (!hasExplicitStaffAssignment && !roleTokens.length) {
    return isUserOnStudyStaff(staffId, staffList);
  }

  return false;
}

/** Tree view: all staff see every pending milestone/placeholder. */
export function studyPlaceholderVisibleInTree() {
  return true;
}

export function studyPlaceholderCanUpload(
  row,
  userData,
  { staffList = [], roleOptions = [] } = {},
) {
  return studyPlaceholderAssignedToUser(row, userData, { staffList, roleOptions });
}

export function studyPlaceholderVisibleToUser(row, userData, { canManage = false } = {}) {
  if (!row || canManage) return true;
  if (!userData?.StaffId) return false;

  const isAdHoc =
    row.IsAdHoc === true ||
    row.IsAdHoc === 1 ||
    row.isAdHoc === true ||
    row.isAdHoc === 1;

  if (!isAdHoc) return true;

  const assignedTo = String(row.AssignedToStaffId || row.assignedToStaffId || "").toLowerCase();
  return assignedTo && assignedTo === String(userData.StaffId).toLowerCase();
}

export function isActiveStudyDocument(doc) {
  if (!doc) return false;
  const status = doc.FileStatus;
  if (status === 1 || status === "1" || status === true) return false;
  return status === 0 || status === "0" || status == null || status === "";
}

export function isSupersededStudyDocument(doc) {
  if (!doc) return false;
  const status = doc.FileStatus;
  return status === 1 || status === "1" || status === true;
}

function collectPlaceholderLookupIds(placeholder, placeholders = []) {
  const ids = new Set();
  const primary = stripGuid(
    placeholder?.StudyPlaceholderId ?? placeholder?.studyPlaceholderId,
  ).toLowerCase();
  if (primary) ids.add(primary);

  const slotKey = primary ? placeholderTreeSlotKey(placeholder) : null;
  if (slotKey) {
    (placeholders || []).forEach((row) => {
      if (placeholderTreeSlotKey(row) === slotKey) {
        const rowId = stripGuid(row.StudyPlaceholderId).toLowerCase();
        if (rowId) ids.add(rowId);
      }
    });
  }

  return ids;
}

export function normalizeDocumentDisplayName(value) {
  return String(value ?? "").trim();
}

export function normalizeDocumentNameForCompare(value) {
  return normalizeDocumentDisplayName(value).toLowerCase();
}

function readExternalDocumentId(doc) {
  return String(
    doc?.ExtDocumentId ??
      doc?.extDocumentId ??
      doc?.DocumentCode ??
      doc?.documentCode ??
      "",
  ).trim();
}

/** Active document in folder with the same display name (case-insensitive). */
export function findDuplicateDocumentNameInFolder(
  documents = [],
  fileName,
  folderId,
  { excludeDocumentId } = {},
) {
  const nameNorm = normalizeDocumentNameForCompare(fileName);
  if (!nameNorm) return null;
  const folderNorm = stripGuid(folderId).toLowerCase();
  const excludeNorm = stripGuid(excludeDocumentId).toLowerCase();

  return (
    (documents || []).find((doc) => {
      if (!isActiveStudyDocument(doc)) return false;
      if (
        folderNorm &&
        stripGuid(doc.FolderIdRef).toLowerCase() !== folderNorm
      ) {
        return false;
      }
      if (
        excludeNorm &&
        stripGuid(doc.DocumentIdRef).toLowerCase() === excludeNorm
      ) {
        return false;
      }
      return (
        normalizeDocumentNameForCompare(doc.FileName) === nameNorm
      );
    }) || null
  );
}

/** Active document in study with the same external document ID. */
export function findDuplicateExternalDocumentId(
  documents = [],
  documentCode,
  { excludeDocumentId } = {},
) {
  const codeNorm = String(documentCode || "").trim().toUpperCase();
  if (!codeNorm) return null;
  const excludeNorm = stripGuid(excludeDocumentId).toLowerCase();

  return (
    (documents || []).find((doc) => {
      if (!isActiveStudyDocument(doc)) return false;
      if (
        excludeNorm &&
        stripGuid(doc.DocumentIdRef).toLowerCase() === excludeNorm
      ) {
        return false;
      }
      const ext = readExternalDocumentId(doc).toUpperCase();
      return ext && ext === codeNorm;
    }) || null
  );
}

/** Suggest a non-conflicting name in the target folder, e.g. report (2).pdf */
export function buildSuggestedUniqueDocumentName(
  baseName,
  documents = [],
  folderId,
) {
  const base = normalizeDocumentDisplayName(baseName);
  if (!base) return "";
  if (!findDuplicateDocumentNameInFolder(documents, base, folderId)) {
    return base;
  }

  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : "";

  for (let i = 2; i <= 99; i += 1) {
    const candidate = `${stem} (${i})${ext}`;
    if (!findDuplicateDocumentNameInFolder(documents, candidate, folderId)) {
      return candidate;
    }
  }

  return `${stem} (${Date.now()})${ext}`;
}

/** ddMMyyHHmmss e.g. 010626232628 */
function formatUploadDocumentNameTimestamp(date = new Date()) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${dd}${mm}${yy}${hh}${min}${ss}`;
}

/** Milestone / ad-hoc code used in upload file names (SSV, T2, DC1, …). */
export function getStudyPlaceholderUploadCode(placeholderRow) {
  if (!placeholderRow) return "";
  return String(
    placeholderRow.MilestoneCode ?? placeholderRow.milestoneCode ?? "",
  ).trim();
}

function sanitizeUploadNameSuffix(value) {
  return String(value || "")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, "_");
}

/** Upload display name: [CODE - ]filename_ddMMyyHHmmss.ext */
export function buildUniqueUploadDocumentName(
  rawFileName,
  { placeholderRow, date = new Date() } = {},
) {
  const fileName = normalizeDocumentDisplayName(rawFileName);
  if (!fileName) return "";

  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : "";
  const code = sanitizeUploadNameSuffix(
    getStudyPlaceholderUploadCode(placeholderRow),
  );
  const stamp = formatUploadDocumentNameTimestamp(date);

  const labelStem = code ? `${code} - ${stem}` : stem;
  return `${labelStem}_${stamp}${ext}`;
}

/** Proposed display name on file pick — plain name with optional milestone prefix. */
export function buildInitialUploadDocumentName(
  rawFileName,
  { placeholderRow } = {},
) {
  const fileName = normalizeDocumentDisplayName(rawFileName);
  if (!fileName) return "";

  if (placeholderRow) {
    const code = String(
      placeholderRow.MilestoneCode || placeholderRow.milestoneCode || "",
    ).trim();
    if (code) {
      return `${code} - ${fileName}`;
    }
  }

  return fileName;
}

/** Default upload label with automatic timestamp suffix. */
export function buildDefaultUploadDocumentName(
  rawFileName,
  { placeholderRow, date = new Date() } = {},
) {
  return buildUniqueUploadDocumentName(rawFileName, { placeholderRow, date });
}

export function validateUploadDocumentName(
  fileName,
  documents = [],
  folderId,
  { checkDuplicate = true } = {},
) {
  const trimmed = normalizeDocumentDisplayName(fileName);
  if (!trimmed) {
    return "Document name is required.";
  }
  if (trimmed.length > 255) {
    return "Document name must be 255 characters or fewer.";
  }
  if (/[<>:"/\\|?*]/.test(trimmed)) {
    return 'Document name cannot contain \\ / : * ? " < > |';
  }
  if (checkDuplicate) {
    const duplicate = findDuplicateDocumentNameInFolder(
      documents,
      trimmed,
      folderId,
    );
    if (duplicate) {
      return "A document with this name already exists in this folder.";
    }
  }
  return "";
}

export function validateUploadExternalDocumentId(
  documents = [],
  documentCode,
  { checkDuplicate = true, excludeDocumentId } = {},
) {
  const trimmed = String(documentCode || "").trim();
  if (!trimmed) {
    return "External document ID is required.";
  }
  if (trimmed.length > 10) {
    return "External document ID must be 10 characters or fewer.";
  }
  if (checkDuplicate) {
    const duplicate = findDuplicateExternalDocumentId(documents, trimmed, {
      excludeDocumentId,
    });
    if (duplicate) {
      return "This external document ID is already used in this study.";
    }
  }
  return "";
}

/** True only when placeholder ↔ document link exists in API data (not heuristic). */
export function hasStrictPlaceholderDocumentLink(placeholder, documents = []) {
  if (!placeholder) return false;
  const docId = stripGuid(placeholder.DocumentIdRef).toLowerCase();
  if (
    docId &&
    (documents || []).some(
      (d) =>
        isActiveStudyDocument(d) &&
        stripGuid(d.DocumentIdRef).toLowerCase() === docId,
    )
  ) {
    return true;
  }
  const phId = stripGuid(
    placeholder.StudyPlaceholderId ?? placeholder.studyPlaceholderId,
  ).toLowerCase();
  if (!phId) return false;
  return (documents || []).some((d) => {
    if (!isActiveStudyDocument(d)) return false;
    return (
      stripGuid(d.StudyPlaceholderId ?? d.studyPlaceholderId).toLowerCase() ===
      phId
    );
  });
}

/** Resolve tree parent id for a document linked to a study placeholder slot. */
function resolveLinkedDocumentTreeParentId(placeholder) {
  if (!placeholder) return null;
  return getStudyPlaceholderTreeNodeId(placeholder);
}

/** Map document id → placeholder tree parent id for nesting files under slots. */
function buildDocumentPlaceholderParentMap(placeholders = [], documents = []) {
  const phList = placeholders || [];
  const docParentMap = new Map();
  const usedDocIds = new Set();

  const assignDocToPlaceholder = (doc, placeholder) => {
    if (!doc || !placeholder) return;
    const docKey = stripGuid(doc.DocumentIdRef).toLowerCase();
    const parentId = resolveLinkedDocumentTreeParentId(placeholder);
    if (!docKey || !parentId || usedDocIds.has(docKey)) return;
    docParentMap.set(docKey, parentId);
    usedDocIds.add(docKey);
  };

  phList.forEach((ph) => {
    if (String(ph.Status || "").toLowerCase() !== "fulfilled") return;

    const docId = stripGuid(ph.DocumentIdRef).toLowerCase();
    if (docId) {
      const doc = (documents || []).find(
        (d) =>
          isActiveStudyDocument(d) &&
          stripGuid(d.DocumentIdRef).toLowerCase() === docId,
      );
      if (doc) assignDocToPlaceholder(doc, ph);
    }

    const fulfilledName = String(ph.FulfilledDocumentName || "")
      .trim()
      .toLowerCase();
    if (fulfilledName) {
      const byName = (documents || []).find(
        (d) =>
          isActiveStudyDocument(d) &&
          String(d.FileName || "").trim().toLowerCase() === fulfilledName,
      );
      if (byName) assignDocToPlaceholder(byName, ph);
    }

    const phId = stripGuid(ph.StudyPlaceholderId).toLowerCase();
    if (phId) {
      (documents || []).forEach((doc) => {
        if (!isActiveStudyDocument(doc)) return;
        if (
          stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId).toLowerCase() ===
          phId
        ) {
          assignDocToPlaceholder(doc, ph);
        }
      });
    }
  });

  const needingDoc = phList
    .filter(
      (ph) =>
        String(ph.Status || "").toLowerCase() === "fulfilled" &&
        !hasStrictPlaceholderDocumentLink(ph, documents),
    )
    .sort((a, b) => {
      const aTime = new Date(a.FulfilledDateTime || a.DueDate || 0).getTime();
      const bTime = new Date(b.FulfilledDateTime || b.DueDate || 0).getTime();
      return aTime - bTime;
    });

  needingDoc.forEach((ph) => {
    const orphans = findUnlinkedFolderDocuments(ph, documents, phList).filter(
      (doc) => !usedDocIds.has(stripGuid(doc.DocumentIdRef).toLowerCase()),
    );
    if (!orphans.length) return;

    if (orphans.length === 1) {
      assignDocToPlaceholder(orphans[0], ph);
      return;
    }

    const assignedStaff = stripGuid(
      ph.AssignedToStaffId ?? ph.assignedToStaffId,
    ).toLowerCase();
    if (assignedStaff) {
      const byStaff = orphans.filter(
        (doc) =>
          stripGuid(doc.StaffIdRef ?? doc.staffIdRef).toLowerCase() ===
          assignedStaff,
      );
      if (byStaff.length === 1) {
        assignDocToPlaceholder(byStaff[0], ph);
        return;
      }
    }

    assignDocToPlaceholder(orphans[0], ph);
  });

  return docParentMap;
}

export function isAdHocStudyPlaceholder(row) {
  if (!row || typeof row !== "object") return false;
  if (
    row.IsAdHoc === true ||
    row.IsAdHoc === 1 ||
    row.IsAdHoc === "1" ||
    row.isAdHoc === true ||
    row.isAdHoc === 1
  ) {
    return true;
  }
  if (row.AdHocBatchId || row.adHocBatchId) return true;
  if (
    String(row.AssignmentMode || "").toLowerCase() === "perstaff" &&
    row.AssignedToStaffId &&
    !row.TemplatePlaceholderId
  ) {
    return true;
  }
  return false;
}

function stripAdHocStaffSuffix(placeholderName, staffName) {
  const name = String(placeholderName || "").trim();
  const staff = String(staffName || "").trim();
  if (!name || !staff) return name;
  const suffix = ` - ${staff}`;
  if (name.toLowerCase().endsWith(suffix.toLowerCase())) {
    return name.slice(0, name.length - suffix.length).trim();
  }
  return name;
}

/** Base document name for ad-hoc placeholders (without per-staff suffix). */
export function getAdHocPlaceholderDisplayName(row) {
  const milestoneName = String(row?.MilestoneName || "").trim();
  if (milestoneName) return milestoneName;
  const staff = String(row?.AssignedToName || "").trim();
  const stripped = stripAdHocStaffSuffix(row?.PlaceholderName, staff);
  if (stripped) return stripped;
  if (row?.AssignedToStaffId && String(row?.PlaceholderName || "").includes(" - ")) {
    return String(row.PlaceholderName).split(" - ")[0].trim();
  }
  return String(row?.PlaceholderName || "").trim();
}

/** Tree / list label: milestone code + document name (not assignee). */
export function formatStudyPlaceholderTreeLabel(row) {
  if (!row) return "Expected document";
  if (isAdHocStudyPlaceholder(row)) {
    const code = String(row.MilestoneCode || "").trim();
    const name = getAdHocPlaceholderDisplayName(row);
    if (code && name) return `${code} — ${name}`;
    if (code) return code;
    return name || "Expected document";
  }
  return String(row.PlaceholderName || row.MilestoneName || "Expected document").trim();
}

export function isMilestoneLinkedStudyPlaceholder(row) {
  if (!row || typeof row !== "object") return false;
  if (isAdHocStudyPlaceholder(row)) {
    return false;
  }
  if (row.MilestoneId || row.milestoneId) return true;
  const code = String(row.MilestoneCode ?? row.milestoneCode ?? "").trim();
  return code.length > 0;
}

export function formatStudyPlaceholderDueOffset(row) {
  if (!row || typeof row !== "object") return "";
  const directRule = String(row?.DueAnchorRule ?? row?.dueAnchorRule ?? "").trim();
  if (directRule) return directRule;

  if (!isMilestoneLinkedStudyPlaceholder(row)) return "";

  const anchorType = String(row?.DueAnchorType ?? row?.dueAnchorType ?? "").trim();
  const anchorCode = String(
    row?.DueAnchorMilestoneCode ?? row?.dueAnchorMilestoneCode ?? "",
  ).trim();
  const offsetRaw = row?.DueOffsetDays ?? row?.dueOffsetDays;
  const offset =
    offsetRaw === "" || offsetRaw == null ? null : Number(offsetRaw);

  if (anchorType === "AnotherMilestone" && anchorCode) {
    if (offset != null && !Number.isNaN(offset) && offset !== 0) {
      return offset > 0 ? `${anchorCode} +${offset}d` : `${anchorCode} ${offset}d`;
    }
    return anchorCode;
  }

  if (anchorType === "ReferenceDate") {
    if (offset != null && !Number.isNaN(offset) && offset !== 0) {
      return offset > 0 ? `Reference date +${offset}d` : `Reference date ${offset}d`;
    }
    return "Reference date";
  }

  if (offset != null && !Number.isNaN(offset)) {
    if (offset === 0) return "Due on milestone date";
    return offset > 0
      ? `Due ${offset} day(s) after milestone`
      : `Due ${Math.abs(offset)} day(s) before milestone`;
  }
  return "";
}

export function studyPlaceholderAwaitingMilestoneDates(row) {
  if (!isMilestoneLinkedStudyPlaceholder(row)) return false;
  const dueDate = row?.DueDate ?? row?.dueDate;
  return !dueDate;
}

export function dueDaysFromDueDate(dueDate) {
  if (!dueDate) return "";
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
  return diff >= 0 ? String(diff) : "";
}

export function dueDateFromDueDays(dueDays) {
  const days = sanitizeDueDaysInput(dueDays);
  if (days === "") return null;
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + Number(days));
  return d.toISOString();
}

function sanitizeDueDaysInput(value) {
  if (value === "" || value == null) return "";
  return String(value).replace(/\D/g, "");
}

export function studyPlaceholderMatchesFolder(row, folderId) {
  if (!folderId || !row) return false;
  return normFolderId(row.FolderIdRef) === normFolderId(folderId);
}

/**
 * Selected-site views require an exact SiteIdRef match.
 * Another site's fulfilled placeholder must not appear under the active site.
 */
export function studyPlaceholderMatchesSite(row, siteIdRef) {
  if (!siteIdRef) return true;
  const site = normFolderId(siteIdRef);
  if (!site) return true;
  const rowSite = normFolderId(row?.SiteIdRef || row?.siteIdRef);
  return Boolean(rowSite) && rowSite === site;
}

export function studyPlaceholderRequiresSignature(row) {
  if (!row || typeof row !== "object") return false;
  if (
    row.RequiresSignature === true ||
    row.RequiresSignature === 1 ||
    row.RequiresSignature === "1" ||
    row.requiresSignature === true ||
    row.requiresSignature === 1
  ) {
    return true;
  }
  const signerRoles = String(row?.SignerRoles ?? row?.signerRoles ?? "").trim();
  return signerRoles.length > 0;
}

function documentHasSignature(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (
    doc.Signed === 1 ||
    doc.Signed === true ||
    doc.Signed === "1" ||
    String(doc.SignedDateTime || "").trim() !== ""
  ) {
    return true;
  }
  return parseSignedIdsFromDocument(doc).length > 0;
}

function placeholderRowHasSignature(row) {
  if (!row || typeof row !== "object") return false;
  if (
    row.IsSigned === 1 ||
    row.IsSigned === true ||
    row.IsSigned === "1" ||
    row.SignatureState === "signed"
  ) {
    return true;
  }
  const signedCount = Number(row.SignedCount ?? row.signedCount ?? 0);
  return signedCount > 0;
}

/**
 * Dashboard / report signature cell: required vs signed vs awaiting.
 * Uses the uploaded document (and its EISF_SignatureDocument rows) first,
 * then any IsSigned / SignedCount already present on the placeholder row.
 */
export function resolvePlaceholderSignatureState(placeholder, doc) {
  const reqSig = studyPlaceholderRequiresSignature(placeholder);
  if (!reqSig) return { reqSig: false, sig: "na" };

  const uploaded = isPlaceholderUploadComplete(placeholder, doc);
  if (!uploaded) return { reqSig: true, sig: "await" };

  const signedIds = parseSignedIdsFromDocument(doc);
  const mandatoryIds = parseMandatorySignerIdsFromDocument(doc, []);
  if (mandatoryIds.length > 0) {
    const signedSet = new Set(signedIds.map((id) => normalizeStaffId(id)));
    const allSigned = mandatoryIds.every((id) => signedSet.has(normalizeStaffId(id)));
    return { reqSig: true, sig: allSigned ? "signed" : "await" };
  }

  if (documentHasSignature(doc) || placeholderRowHasSignature(placeholder)) {
    return { reqSig: true, sig: "signed" };
  }
  return { reqSig: true, sig: "await" };
}

export function decorateReadinessRowsWithSignatures(
  rows = [],
  placeholders = [],
  documents = [],
) {
  const phById = new Map();
  (placeholders || []).forEach((ph) => {
    const key = studyPlaceholderIdKey(ph);
    if (key) phById.set(key, ph);
  });

  return (rows || []).map((row) => {
    const key = studyPlaceholderIdKey(row);
    const ph = { ...row, ...(phById.get(key) || {}) };
    const doc = resolveMilestoneLinkedDocument(ph, documents);
    const { reqSig, sig } = resolvePlaceholderSignatureState(ph, doc);
    return {
      ...row,
      ...ph,
      RequiresSignature: reqSig ? 1 : 0,
      SignatureState: sig,
      IsSigned: sig === "signed" ? 1 : 0,
    };
  });
}

export function decorateReadinessReportSignatures(
  report,
  placeholders = [],
  documents = [],
) {
  if (!report) return report;
  const placeholderDetails = decorateReadinessRowsWithSignatures(
    report.placeholderDetails,
    placeholders,
    documents,
  );
  return {
    ...report,
    placeholderDetails,
    summary: {
      ...(report.summary || {}),
      ...summarizePlaceholderDetails(placeholderDetails),
    },
    milestoneSummary: computeMilestoneSummaryFromDetails(placeholderDetails),
    siteComparison: computeSiteComparisonFromDetails(placeholderDetails),
  };
}

/** Resolve the study placeholder linked to an uploaded document row. */
export function findStudyPlaceholderForDocument(doc, placeholders = []) {
  if (!doc || !Array.isArray(placeholders) || !placeholders.length) return null;

  const phId = doc.StudyPlaceholderId ?? doc.studyPlaceholderId;
  if (phId) {
    const byId = placeholders.find(
      (p) => String(p.StudyPlaceholderId) === String(phId),
    );
    if (byId) return byId;
  }

  const docId = doc.DocumentIdRef ?? doc.id;
  if (docId) {
    const norm = stripGuid(docId).toLowerCase();
    const byDoc = placeholders.find(
      (p) => stripGuid(p.DocumentIdRef).toLowerCase() === norm,
    );
    if (byDoc) return byDoc;
  }

  const phName = String(doc.PlaceholderName ?? doc.placeholderName ?? "").trim();
  if (phName) {
    const folderId = stripGuid(doc.FolderIdRef ?? doc.parentId).toLowerCase();
    const byName = placeholders.find((p) => {
      if (String(p.PlaceholderName || "").trim().toLowerCase() !== phName.toLowerCase()) {
        return false;
      }
      if (!folderId) return true;
      return stripGuid(p.FolderIdRef).toLowerCase() === folderId;
    });
    if (byName) return byName;
  }

  return null;
}

function isGuidLikeString(value) {
  if (value == null || value === "") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value).trim(),
  );
}

function roleDisplayNameFromOptions(roleId, roleOptions = []) {
  const norm = normalizeStaffId(roleId);
  if (!norm) return "";
  const match = (roleOptions || []).find(
    (r) => normalizeStaffId(r.roleId ?? r.value ?? r.RoleId) === norm,
  );
  const name = (
    match?.roleName ??
    match?.label ??
    match?.name ??
    match?.Roles ??
    ""
  ).trim();
  if (name && !isGuidLikeString(name)) return name;
  return "";
}

/** Resolve role CSV / GUID tokens to display rows (never surface raw GUIDs). */
export function resolvePlaceholderRoleNameEntries(raw, roleOptions = []) {
  const tokens = String(raw || "")
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean);
  const seen = new Set();
  const entries = [];

  tokens.forEach((token) => {
    const resolvedIds = resolveRecipientRoleIds(token, roleOptions);
    const ids = resolvedIds.length
      ? resolvedIds
      : isGuidLikeString(token)
        ? [token]
        : [];

    if (ids.length) {
      ids.forEach((roleId) => {
        const key = normalizeStaffId(roleId);
        if (!key || seen.has(key)) return;
        seen.add(key);
        const roleName = roleDisplayNameFromOptions(roleId, roleOptions);
        if (roleName) entries.push({ roleId: key, roleName });
      });
      return;
    }

    if (!isGuidLikeString(token)) {
      const key = token.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({ roleId: key, roleName: token });
    }
  });

  return entries;
}

/** Human-readable role labels from placeholder SignerRoles / DefaultAssigneeRole CSV. */
export function formatPlaceholderRoleLabels(raw, roleOptions = []) {
  return resolvePlaceholderRoleNameEntries(raw, roleOptions)
    .map((entry) => entry.roleName)
    .join(", ");
}

/** Document uploaded / placeholder fulfilled. */
export function isPlaceholderUploadComplete(placeholder, doc) {
  const phStatus = String(placeholder?.Status ?? placeholder?.status ?? "")
    .trim()
    .toLowerCase();
  if (phStatus === "fulfilled") return true;
  if (stripGuid(placeholder?.DocumentIdRef ?? placeholder?.documentIdRef)) {
    return true;
  }
  return !!stripGuid(doc?.DocumentIdRef ?? doc?.id);
}

/** Two-factor completeness: upload (50%) + proportional signature (50%). */
export function computePlaceholderMilestoneCompleteness({
  requiresSignature,
  uploadComplete,
  signedCount = 0,
  totalSigners = 0,
  signComplete,
}) {
  if (!requiresSignature) {
    return {
      percent: uploadComplete ? 100 : 0,
      uploadComplete: !!uploadComplete,
      signComplete: true,
      signProgress: 1,
      signedCount: 0,
      totalSigners: 0,
      requiresSignature: false,
    };
  }

  const uploadWeight = 0.5;
  const signWeight = 0.5;
  const uploadPortion = uploadComplete ? uploadWeight : 0;
  const signProgress =
    totalSigners > 0
      ? signedCount / totalSigners
      : signComplete
        ? 1
        : 0;
  const signPortion = signWeight * signProgress;
  const percent = Math.round((uploadPortion + signPortion) * 100);
  const resolvedSignComplete =
    totalSigners > 0 ? signedCount >= totalSigners : !!signComplete;

  return {
    percent,
    uploadComplete: !!uploadComplete,
    signComplete: resolvedSignComplete,
    signProgress,
    signedCount,
    totalSigners,
    requiresSignature: true,
  };
}

export function parseSignedIdsFromDocument(doc) {
  if (!doc || typeof doc !== "object") return [];
  const raw =
    doc.SignedUserIds ||
    doc.signedIds ||
    doc.signedUserIds ||
    doc.SignedIds ||
    doc.SignedByIds ||
    doc.signedByIds ||
    doc.SignedUserId ||
    doc.signedUserId ||
    "";
  const fromCsv = parseCsvIds(raw);
  if (fromCsv.length) return fromCsv;

  const signed =
    doc.Signed === 1 ||
    doc.Signed === true ||
    doc.Signed === "1" ||
    String(doc.SignedDateTime || "").trim() !== "";
  if (!signed) return [];

  const directId =
    doc.SignerStaffId ||
    doc.SignatureStaffId ||
    doc.sdStaffId ||
    doc.SignatureStaffIdRef;
  if (directId) {
    const normalized = normalizeStaffId(directId);
    return normalized ? [normalized] : [];
  }
  return [];
}

function parseMandatorySignerIdsFromDocument(doc, fallbackIds = []) {
  if (!doc || typeof doc !== "object") {
    return (fallbackIds || []).map((id) => normalizeStaffId(id)).filter(Boolean);
  }
  const raw =
    doc.MandatorySignUserIds ||
    doc.mandatorySignUserIds ||
    doc.MandatorySignUserId ||
    doc.mandatorySignUserId ||
    "";
  const fromDoc = parseCsvIds(raw);
  if (fromDoc.length) return fromDoc;
  return (fallbackIds || []).map((id) => normalizeStaffId(id)).filter(Boolean);
}

/** Per-placeholder mandatory signers + signed progress. */
export function resolvePlaceholderSignProgress(
  placeholder,
  staffList = [],
  roleOptions = [],
  documents = [],
) {
  const doc = resolveMilestoneLinkedDocument(placeholder, documents);
  const signConfig = buildMandatorySignUsersFromPlaceholder(
    placeholder,
    staffList,
    roleOptions,
  );
  const mandatorySignUsers = signConfig.mandatorySignUsers || [];
  const fallbackIds = mandatorySignUsers
    .map((user) => normalizeStaffId(user.value))
    .filter(Boolean);
  const mandatorySignerIds = parseMandatorySignerIdsFromDocument(doc, fallbackIds);
  const signedIds = parseSignedIdsFromDocument(doc);
  const signedSet = new Set(signedIds);
  const signedCount = mandatorySignerIds.filter((id) => signedSet.has(id)).length;
  const totalSigners = mandatorySignerIds.length;

  return {
    document: doc,
    mandatorySignUsers,
    mandatorySignerIds,
    signedIds,
    signedCount,
    totalSigners,
    signComplete: totalSigners > 0 && signedCount >= totalSigners,
    signProgress: totalSigners > 0 ? signedCount / totalSigners : 0,
  };
}

/** Milestone-level completeness averaged across all linked placeholders. */
export function computeMilestoneConsolidatedCompleteness(
  placeholders,
  { staffList = [], roleOptions = [], documents = [] } = {},
) {
  const active = (placeholders || []).filter(
    (p) => String(p?.Status || "").toLowerCase() !== "waived",
  );

  if (!active.length) {
    return {
      percent: 0,
      uploadComplete: false,
      signComplete: false,
      signProgress: 0,
      signedCount: 0,
      totalSigners: 0,
      uploadDoneCount: 0,
      uploadTotalCount: 0,
      requiresSignature: false,
      placeholderCount: 0,
      placeholdersComplete: 0,
    };
  }

  const details = active.map((ph) => {
    const doc = resolveMilestoneLinkedDocument(ph, documents);
    const uploadComplete = isPlaceholderUploadComplete(ph, doc);
    const requiresSignature = studyPlaceholderRequiresSignature(ph);
    const sign = resolvePlaceholderSignProgress(
      ph,
      staffList,
      roleOptions,
      documents,
    );
    const completeness = computePlaceholderMilestoneCompleteness({
      requiresSignature,
      uploadComplete,
      signedCount: sign.signedCount,
      totalSigners: sign.totalSigners,
      signComplete: sign.signComplete,
    });
    return { placeholder: ph, sign, completeness };
  });

  const percent = Math.round(
    details.reduce((sum, item) => sum + item.completeness.percent, 0) /
      details.length,
  );
  const requiresSignature = details.some((d) => d.completeness.requiresSignature);
  const uploadComplete = details.every((d) => d.completeness.uploadComplete);
  const signedCount = details.reduce((sum, d) => sum + d.sign.signedCount, 0);
  const totalSigners = details.reduce((sum, d) => sum + d.sign.totalSigners, 0);
  const signProgress = totalSigners > 0 ? signedCount / totalSigners : 0;
  const signComplete =
    !requiresSignature ||
    details.every(
      (d) =>
        !d.completeness.requiresSignature || d.completeness.signComplete,
    );

  return {
    percent,
    uploadComplete,
    signComplete,
    signProgress,
    signedCount,
    totalSigners,
    uploadDoneCount: details.filter((d) => d.completeness.uploadComplete).length,
    uploadTotalCount: active.length,
    requiresSignature,
    placeholderCount: active.length,
    placeholdersComplete: details.filter((d) => d.completeness.percent >= 100)
      .length,
    details,
  };
}

/** Individual signer rows for a milestone (who signed / who is pending). */
export function buildMilestoneSignerStaffStatusRows(
  placeholders,
  staffList = [],
  roleOptions = [],
  documents = [],
) {
  const buckets = new Map();

  (placeholders || []).forEach((ph) => {
    if (String(ph?.Status || "").toLowerCase() === "waived") return;
    if (!studyPlaceholderRequiresSignature(ph)) return;

    const sign = resolvePlaceholderSignProgress(
      ph,
      staffList,
      roleOptions,
      documents,
    );
    const signedSet = new Set(sign.signedIds);

    sign.mandatorySignUsers.forEach((user) => {
      const staffId = normalizeStaffId(user.value);
      if (!staffId) return;

      const staff = (staffList || []).find(
        (row) => normalizeStaffId(row.value ?? row.id ?? row.StaffId) === staffId,
      );
      const staffName =
        user.label || staff?.label || staff?.name || staff?.StaffName || "Staff";
      const roleName = staff?.Role || staff?.roleName || "";

      if (!buckets.has(staffId)) {
        buckets.set(staffId, {
          staffId,
          staffName,
          roleName,
          requiredCount: 0,
          signedCount: 0,
        });
      }

      const bucket = buckets.get(staffId);
      bucket.requiredCount += 1;
      if (signedSet.has(staffId)) bucket.signedCount += 1;
    });
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.staffName.localeCompare(b.staffName, undefined, { sensitivity: "base" }))
    .map((row) => ({
      staffName: row.staffName,
      roleName: row.roleName,
      displayName: row.staffName,
      status:
        row.requiredCount > 0 && row.signedCount >= row.requiredCount
          ? "Completed"
          : "Pending",
      signedCount: row.signedCount,
      requiredCount: row.requiredCount,
    }));
}

/** Uploader rows across all milestone placeholders. */
export function buildMilestoneUploaderStatusRows(
  placeholders,
  roleOptions = [],
  documents = [],
) {
  const active = (placeholders || []).filter(
    (p) => String(p?.Status || "").toLowerCase() !== "waived",
  );

  return active.flatMap((ph) => {
    const doc = resolveMilestoneLinkedDocument(ph, documents);
    const uploadComplete = isPlaceholderUploadComplete(ph, doc);
    const rows = buildUploaderRoleStatusRows(ph, roleOptions, uploadComplete);
    const prefix =
      active.length > 1 ? String(ph.PlaceholderName || "Document").trim() : "";

    return rows.map((row) => ({
      ...row,
      displayName: prefix ? `${prefix} · ${row.roleName}` : row.roleName,
    }));
  });
}

/** Configured uploader roles (template / milestone), then study AssigneeRole override. */
export function resolvePlaceholderUploaderRoleCsv(placeholder = {}) {
  const configured = String(
    placeholder?.DefaultAssigneeRole ?? placeholder?.defaultAssigneeRole ?? "",
  ).trim();
  if (configured) return configured;
  return String(
    placeholder?.AssigneeRole ?? placeholder?.assigneeRole ?? "",
  ).trim();
}

export function buildUploaderRoleStatusRows(
  placeholder,
  roleOptions = [],
  uploadComplete = false,
) {
  const entries = resolvePlaceholderRoleNameEntries(
    resolvePlaceholderUploaderRoleCsv(placeholder),
    roleOptions,
  );

  if (entries.length) {
    return entries.map((entry) => ({
      roleName: entry.roleName,
      roleId: entry.roleId,
      status: uploadComplete ? "Completed" : "Pending",
    }));
  }

  /* Legacy fallback when only human-readable names exist (never use StaffRoles.Roles abbreviations). */
  const assigneeNames = String(placeholder?.AssigneeRoleName ?? "").trim();
  if (assigneeNames && !isGuidLikeString(assigneeNames)) {
    return assigneeNames
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
      .filter((roleName) => roleName.length > 2)
      .map((roleName) => ({
        roleName,
        status: uploadComplete ? "Completed" : "Pending",
      }));
  }

  return [];
}

/** Upload role avatars for the selected placeholder (pending vs done). */
export function buildPlaceholderUploadRoleGroups(
  placeholder,
  roleOptions = [],
  uploadComplete = false,
) {
  const rows = buildUploaderRoleStatusRows(
    placeholder,
    roleOptions,
    uploadComplete,
  );
  const toAvatar = (row) => ({
    id: row.roleId || row.roleName,
    label: row.roleName,
    roleName: row.roleName,
  });

  const done = rows
    .filter((row) => row.status === "Completed")
    .map(toAvatar);
  const pending = rows
    .filter((row) => row.status === "Pending")
    .map(toAvatar);

  return {
    done,
    pending,
    uploadDoneCount: done.length,
    uploadTotalCount: rows.length,
  };
}

/** Signer role avatars for a placeholder (milestone library style). */
export function getPlaceholderSignerRoleAvatars(placeholder, roleOptions = []) {
  return resolvePlaceholderRoleNameEntries(
    placeholder?.SignerRoles ?? placeholder?.signerRoles,
    roleOptions,
  ).map(({ roleId, roleName }) => ({
    id: roleId || roleName,
    label: roleName,
    roleName,
  }));
}

/** Signer role labels for a placeholder. */
export function getPlaceholderSignerRoleLabels(placeholder, roleOptions = []) {
  return getPlaceholderSignerRoleAvatars(placeholder, roleOptions).map(
    (entry) => entry.roleName,
  );
}

/** Signature user avatars for the selected placeholder (done vs pending). */
export function buildPlaceholderSignatureAvatarGroups(
  placeholder,
  staffList = [],
  roleOptions = [],
  documents = [],
) {
  if (!placeholder || !studyPlaceholderRequiresSignature(placeholder)) {
    return {
      done: [],
      pending: [],
      roleAvatars: [],
      roleLabels: [],
      signedCount: 0,
      totalSigners: 0,
      requiresSignature: false,
    };
  }

  const sign = resolvePlaceholderSignProgress(
    placeholder,
    staffList,
    roleOptions,
    documents,
  );
  const signedSet = new Set(sign.signedIds);
  const done = [];
  const pending = [];

  sign.mandatorySignUsers.forEach((user) => {
    const staffId = normalizeStaffId(user.value);
    if (!staffId) return;

    const staff = (staffList || []).find(
      (row) =>
        normalizeStaffId(row.value ?? row.id ?? row.StaffId) === staffId,
    );
    const displayName = String(
      user.label ||
        staff?.label ||
        staff?.name ||
        staff?.StaffName ||
        staff?.UserName ||
        "",
    ).trim();
    if (!displayName) return;

    const roleName = String(staff?.Role || staff?.roleName || "").trim();
    const entry = { staffId, label: displayName, displayName, roleName };

    if (signedSet.has(staffId)) done.push(entry);
    else pending.push(entry);
  });

  return {
    done,
    pending,
    roleAvatars: getPlaceholderSignerRoleAvatars(placeholder, roleOptions),
    roleLabels: getPlaceholderSignerRoleLabels(placeholder, roleOptions),
    signedCount: done.length,
    totalSigners: sign.mandatorySignUsers.length,
    requiresSignature: true,
  };
}

export function buildSignerRoleStatusRows(
  placeholder,
  roleOptions = [],
  staffList = [],
  mandatorySignerIds = [],
  signedIds = [],
) {
  const signedSet = new Set(
    (signedIds || []).map((id) => normalizeStaffId(id)).filter(Boolean),
  );
  const mandatorySet = new Set(
    (mandatorySignerIds || []).map((id) => normalizeStaffId(id)).filter(Boolean),
  );

  return resolvePlaceholderRoleNameEntries(
    placeholder?.SignerRoles ?? placeholder?.signerRoles,
    roleOptions,
  ).map(({ roleId, roleName }) => {
    const staffInRole = (staffList || []).filter(
      (staff) =>
        normalizeStaffId(staff.roleId ?? staff.RoleId) ===
        normalizeStaffId(roleId),
    );
    const requiredSigners = mandatorySet.size
      ? staffInRole.filter((staff) =>
          mandatorySet.has(normalizeStaffId(staff.value ?? staff.id)),
        )
      : staffInRole;

    const complete =
      requiredSigners.length > 0 &&
      requiredSigners.every((staff) =>
        signedSet.has(normalizeStaffId(staff.value ?? staff.id)),
      );

    return {
      roleName,
      status: complete ? "Completed" : "Pending",
    };
  });
}

/** Map study staff + signer roles to mandatory-sign payload for upload (no manual UI). */
export function buildMandatorySignUsersFromPlaceholder(row, staffList = [], roleOptions = []) {
  if (!studyPlaceholderRequiresSignature(row)) {
    return { isMandatorySign: false, mandatorySignUsers: [] };
  }

  const signerRaw = row?.SignerRoles ?? row?.signerRoles ?? "";
  const signerTokens = String(signerRaw)
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean);
  const { roleIds, roleNames } = expandAssigneeRoleTokens(signerTokens, roleOptions);

  const mandatorySignUsers = (staffList || [])
    .filter((staff) => {
      const roleId = normalizeStaffId(staff.roleId || staff.RoleId);
      if (roleId && roleIds.has(roleId)) return true;
      const roleName = normalizeRoleName(staff.Role || staff.roleName);
      return roleName && roleNames.has(roleName);
    })
    .map((staff) => ({
      value: staff.value || staff.id || staff.StaffId,
      label: staff.label || staff.name || staff.StaffName || "Staff",
    }));

  return { isMandatorySign: true, mandatorySignUsers };
}

export function isPendingStudyPlaceholder(
  p,
  documents = null,
  placeholders = [],
) {
  if (!p) return false;
  const docList = Array.isArray(documents) ? documents : null;
  const row = docList?.length
    ? reconcileStudyPlaceholderRow(p, docList, placeholders)
    : p;
  const status = String(
    row?.Status ?? row?.status ?? row?.placeholderStatus ?? "",
  ).toLowerCase();
  if (status === "waived") return false;
  if (status === "fulfilled") return false;
  if (stripGuid(row?.DocumentIdRef ?? row?.documentIdRef)) return false;
  if (docList?.length && findDocumentForStudyPlaceholder(row, docList, placeholders)) {
    return false;
  }
  return true;
}

function buildStudyMilestoneTreeNodeId(milestoneId, bucketKey) {
  if (milestoneId) {
    return `study-ms-${stripGuid(milestoneId).toLowerCase()}`;
  }
  return `study-ms-${String(bucketKey).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`;
}

/** All study placeholders linked to a milestone tree node or milestone metadata. */
export function findStudyPlaceholdersForMilestone(
  milestoneRef,
  studyPlaceholders = [],
) {
  if (!milestoneRef || !Array.isArray(studyPlaceholders)) return [];

  const msId = stripGuid(
    milestoneRef.milestoneId ?? milestoneRef.MilestoneId,
  )?.toLowerCase();
  const msCode = String(
    milestoneRef.milestoneCode ?? milestoneRef.MilestoneCode ?? "",
  )
    .trim()
    .toUpperCase();
  const folderId = stripGuid(
    milestoneRef.parentId ??
      milestoneRef.FolderIdRef ??
      milestoneRef.folderId,
  )?.toLowerCase();

  if (!msId && !msCode) return [];

  return studyPlaceholders.filter((p) => {
    if (!isMilestoneLinkedStudyPlaceholder(p)) return false;
    if (folderId && stripGuid(p.FolderIdRef).toLowerCase() !== folderId) {
      return false;
    }
    const pMsId = stripGuid(p.MilestoneId)?.toLowerCase();
    if (msId && pMsId) return pMsId === msId;
    const pCode = String(p.MilestoneCode || "").trim().toUpperCase();
    return Boolean(msCode && pCode === msCode);
  });
}

function pickPrimaryMilestonePlaceholder(
  placeholders = [],
  documents = null,
  allPlaceholders = [],
) {
  if (!placeholders.length) return null;
  const pending = placeholders.filter((p) =>
    isPendingStudyPlaceholder(p, documents, allPlaceholders),
  );
  const pool = pending.length ? pending : placeholders;
  return pool
    .slice()
    .sort((a, b) =>
      String(a.PlaceholderName || "").localeCompare(
        String(b.PlaceholderName || ""),
        undefined,
        { sensitivity: "base" },
      ),
    )[0];
}

/** Resolve the placeholder that drives milestone info for the current tree selection. */
export function resolveMilestonePlaceholderForSelection(
  node,
  studyPlaceholders = [],
  documents = null,
) {
  if (!node) return null;

  if (node.type === "milestone" || node.isStudyMilestone) {
    return pickPrimaryMilestonePlaceholder(
      findStudyPlaceholdersForMilestone(node, studyPlaceholders),
      documents,
      studyPlaceholders,
    );
  }

  if (
    node.type === "placeholder" ||
    node.studyPlaceholderId ||
    node.StudyPlaceholderId
  ) {
    return resolveStudyPlaceholderRow(node, studyPlaceholders);
  }

  if (node.type === "file" || node.DocumentIdRef || node.VersionNo) {
    return findStudyPlaceholderForDocument(node, studyPlaceholders);
  }

  return null;
}

/** Milestone metadata for any tree selection (milestone, placeholder, or file). */
export function resolveMilestoneRefForSelection(node, studyPlaceholders = []) {
  if (!node) return null;

  if (node.type === "milestone" || node.isStudyMilestone) {
    return node;
  }

  const ph = resolveMilestonePlaceholderForSelection(node, studyPlaceholders);
  if (!ph) return null;

  return {
    type: "milestone",
    milestoneId: ph.MilestoneId,
    milestoneCode: ph.MilestoneCode,
    milestoneName: ph.MilestoneName,
    parentId: stripGuid(ph.FolderIdRef),
    name: [ph.MilestoneCode, ph.MilestoneName].filter(Boolean).join(" — "),
    dueAnchorRule: formatStudyPlaceholderDueOffset(ph),
  };
}

/** Uploaded document row linked to a study placeholder (milestone or ad-hoc). */
export function resolveStudyPlaceholderLinkedDocument(
  placeholder,
  documents = [],
  selectedNode = null,
) {
  const linked = findDocumentForStudyPlaceholder(placeholder, documents);
  if (linked) return linked;
  return resolveMilestoneLinkedDocument(null, documents, selectedNode);
}

/** Uploaded document row linked to the active milestone placeholder, if any. */
export function resolveMilestoneLinkedDocument(
  placeholder,
  documents = [],
  selectedNode = null,
) {
  const docList = asStudyDocumentList(documents);
  if (placeholder) {
    const docId = stripGuid(placeholder.DocumentIdRef);
    if (docId) {
      const match = docList.find(
        (d) =>
          stripGuid(d.DocumentIdRef).toLowerCase() === docId.toLowerCase(),
      );
      if (match) return match;
    }

    const phId = stripGuid(
      placeholder.StudyPlaceholderId ?? placeholder.studyPlaceholderId,
    ).toLowerCase();
    if (phId) {
      const byPhId = docList.find((d) => {
        if (!isActiveStudyDocument(d)) return false;
        return (
          stripGuid(d.StudyPlaceholderId ?? d.studyPlaceholderId).toLowerCase() ===
          phId
        );
      });
      if (byPhId) return byPhId;
    }
  }

  if (
    selectedNode?.type === "file" ||
    selectedNode?.DocumentIdRef ||
    selectedNode?.VersionNo
  ) {
    const docId = stripGuid(selectedNode.DocumentIdRef || selectedNode.id);
    if (!docId) return null;
    return (
      docList.find(
        (d) =>
          stripGuid(d.DocumentIdRef).toLowerCase() === docId.toLowerCase(),
      ) || selectedNode
    );
  }

  return null;
}

/** Tree node id for a study placeholder slot (must match mapSingleStudyPlaceholderTreeNode). */
export function getStudyPlaceholderTreeNodeId(placeholder) {
  const id =
    placeholder?.StudyPlaceholderId ?? placeholder?.studyPlaceholderId;
  const normalized = stripGuid(id);
  if (!normalized) return null;
  return `ph-${normalized.toLowerCase()}`;
}

/** Merge placeholder rows (e.g. site-scoped + study-wide) without duplicates. */
export function mergeStudyPlaceholderLists(...lists) {
  const byId = new Map();
  lists.flat().forEach((row) => {
    if (!row?.StudyPlaceholderId) return;
    const key = stripGuid(row.StudyPlaceholderId).toLowerCase();
    if (!byId.has(key)) byId.set(key, row);
  });
  return Array.from(byId.values());
}

function placeholderTreeSlotKey(row) {
  const folderId = normFolderId(row?.FolderIdRef);
  const siteId = normFolderId(row?.SiteIdRef || row?.siteIdRef);
  const templatePhId = stripGuid(
    row?.TemplatePlaceholderId ?? row?.templatePlaceholderId,
  ).toLowerCase();
  const msId = stripGuid(row?.MilestoneId ?? row?.milestoneId).toLowerCase();
  const msCode = String(row?.MilestoneCode || row?.milestoneCode || "")
    .trim()
    .toUpperCase();
  const assignedTo = stripGuid(
    row?.AssignedToStaffId ?? row?.assignedToStaffId,
  ).toLowerCase();
  const adHocCode = String(row?.MilestoneCode || row?.milestoneCode || "")
    .trim()
    .toUpperCase();

  if (isAdHocStudyPlaceholder(row)) {
    return `adhoc:${folderId}:${siteId}:${adHocCode}:${assignedTo}`;
  }
  if (templatePhId) {
    return `tpl:${folderId}:${siteId}:${templatePhId}:${msId || msCode}`;
  }
  const name = String(row?.PlaceholderName || row?.placeholderName || "")
    .trim()
    .toLowerCase();
  return `slot:${folderId}:${siteId}:${msId || msCode}:${name}`;
}

function placeholderTreeRowScore(row, linkedPhIds = null) {
  let score = 0;
  const rowId = stripGuid(row?.StudyPlaceholderId).toLowerCase();
  if (linkedPhIds?.has(rowId)) score += 500;
  const status = String(row?.Status || row?.status || "").toLowerCase();
  if (stripGuid(row?.DocumentIdRef ?? row?.documentIdRef)) score += 100;
  if (status === "fulfilled") score += 50;
  if (status === "overdue") score += 30;
  if (status === "pending") score += 10;
  if (row?.TemplatePlaceholderId) score += 5;
  if (row?.FolderName) score += 2;
  if (row?.AssignedToName) score += 1;
  return score;
}

function collectLinkedPlaceholderIds(documents = [], placeholders = []) {
  const ids = new Set(
    (documents || [])
      .map((doc) =>
        stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId).toLowerCase(),
      )
      .filter(Boolean),
  );

  (placeholders || []).forEach((row) => {
    const rowId = stripGuid(row.StudyPlaceholderId).toLowerCase();
    if (!rowId) return;
    if (stripGuid(row.DocumentIdRef ?? row.documentIdRef)) {
      ids.add(rowId);
    }
  });

  return ids;
}

/**
 * Merge duplicate folder-document API rows and build SignedUserIds from
 * Signed / SignedBy / SignerStaffId fields returned by the folder query.
 */
export function consolidateDocumentSignatureRows(rows = [], staffList = []) {
  const staffByName = new Map();
  (staffList || []).forEach((staff) => {
    const id = staff.value || staff.id || staff.StaffId;
    const label = String(staff.label || staff.name || "")
      .trim()
      .toLowerCase();
    if (label && id) staffByName.set(label, String(id));
  });

  const byId = new Map();
  (rows || []).forEach((row) => {
    const docId = stripGuid(row.DocumentIdRef).toLowerCase();
    if (!docId) return;

    if (!byId.has(docId)) {
      byId.set(docId, { ...row, signedIds: new Set() });
    }
    const entry = byId.get(docId);

    const signed =
      row.Signed === 1 ||
      row.Signed === true ||
      row.Signed === "1" ||
      String(row.SignedDateTime || "").trim() !== "";
    if (signed) {
      const signerStaffId =
        row.SignerStaffId ||
        row.SignatureStaffId ||
        row.sdStaffId ||
        staffByName.get(String(row.SignedBy || "").trim().toLowerCase());
      if (signerStaffId) {
        entry.signedIds.add(normalizeStaffId(signerStaffId));
      }
    }

    if (row.MandatorySignUserIds) {
      entry.MandatorySignUserIds = row.MandatorySignUserIds;
    }
    if (row.IsMandatorySign != null) {
      entry.IsMandatorySign = row.IsMandatorySign;
    }
  });

  return Array.from(byId.values()).map(({ signedIds, ...doc }) => ({
    ...doc,
    SignedUserIds: Array.from(signedIds).join(","),
  }));
}

export function documentIdentityKey(docOrId) {
  const id =
    typeof docOrId === "string"
      ? docOrId
      : docOrId?.DocumentIdRef || docOrId?.id;
  return stripGuid(id).toLowerCase();
}

/** Local patch after a successful sign API call (before folder refetch completes). */
export function buildDocumentSignatureStatePatch(doc, staffId, staffName) {
  const myId = normalizeStaffId(staffId);
  const signedIds = new Set(parseSignedIdsFromDocument(doc));
  if (myId) signedIds.add(myId);
  const label = String(staffName || "").trim() || "Signer";
  return {
    Signed: 1,
    SignedBy: label,
    SignerStaffId: staffId,
    SignedUserIds: Array.from(signedIds).join(","),
  };
}

export function patchDocumentRowsWithSignature(
  rows = [],
  docId,
  staffId,
  staffName,
) {
  const targetKey = documentIdentityKey(docId);
  if (!targetKey) return rows;
  return (rows || []).map((row) => {
    if (documentIdentityKey(row) !== targetKey) return row;
    return {
      ...row,
      ...buildDocumentSignatureStatePatch(row, staffId, staffName),
    };
  });
}

export function patchTreeNodesWithSignature(
  nodes = [],
  docId,
  staffId,
  staffName,
) {
  const targetKey = documentIdentityKey(docId);
  if (!targetKey) return nodes;

  const visit = (list) =>
    (list || []).map((node) => {
      if (
        node?.type === "file" &&
        documentIdentityKey(node.id || node.DocumentIdRef) === targetKey
      ) {
        return {
          ...node,
          ...buildDocumentSignatureStatePatch(node, staffId, staffName),
          isMandatorySign: node.isMandatorySign,
        };
      }
      if (node?.children?.length) {
        return { ...node, children: visit(node.children) };
      }
      return node;
    });

  return visit(nodes);
}

/** Copy placeholder forward/back links onto document rows when the API omits StudyPlaceholderId. */
export function enrichDocumentsWithPlaceholderLinks(
  documents = [],
  placeholders = [],
) {
  const docById = new Map();

  const enriched = (documents || []).map((doc) => {
    const copy = { ...doc };
    const key = stripGuid(copy.DocumentIdRef).toLowerCase();
    if (key) docById.set(key, copy);
    return copy;
  });

  (placeholders || []).forEach((ph) => {
    const phId = ph?.StudyPlaceholderId ?? ph?.studyPlaceholderId;
    if (!phId) return;

    const docId = stripGuid(ph?.DocumentIdRef ?? ph?.documentIdRef).toLowerCase();
    if (docId && docById.has(docId)) {
      const doc = docById.get(docId);
      if (!stripGuid(doc.StudyPlaceholderId ?? doc.studyPlaceholderId)) {
        doc.StudyPlaceholderId = phId;
      }
      return;
    }

    const fulfilledName = String(
      ph.FulfilledDocumentName ?? ph.fulfilledDocumentName ?? "",
    )
      .trim()
      .toLowerCase();
    if (!fulfilledName) return;

    const byName = enriched.find(
      (doc) =>
        isActiveStudyDocument(doc) &&
        String(doc.FileName || "").trim().toLowerCase() === fulfilledName,
    );
    if (byName && !stripGuid(byName.StudyPlaceholderId ?? byName.studyPlaceholderId)) {
      byName.StudyPlaceholderId = phId;
    }
  });

  enriched.forEach((doc) => {
    const phKey = stripGuid(
      doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
    ).toLowerCase();
    if (!phKey) return;
    const docKey = stripGuid(doc.DocumentIdRef).toLowerCase();
    if (!docKey) return;
    (placeholders || []).forEach((ph) => {
      if (stripGuid(ph.StudyPlaceholderId).toLowerCase() !== phKey) return;
      if (!stripGuid(ph.DocumentIdRef ?? ph.documentIdRef)) {
        ph.DocumentIdRef = doc.DocumentIdRef;
        ph.FulfilledDocumentName = doc.FileName;
      }
    });
  });

  return enriched;
}

/** Point document placeholder ids at the deduped tree row for the same logical slot. */
function alignDocumentsToTreePlaceholders(documents = [], phList = [], allRows = []) {
  const treeIds = new Set(
    (phList || []).map((p) => stripGuid(p.StudyPlaceholderId).toLowerCase()),
  );
  const slotWinners = new Map(
    (phList || []).map((p) => [placeholderTreeSlotKey(p), p]),
  );

  return (documents || []).map((doc) => {
    const phKey = stripGuid(
      doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
    ).toLowerCase();
    if (!phKey || treeIds.has(phKey)) return doc;

    const sourceRow = (allRows || []).find(
      (p) => stripGuid(p.StudyPlaceholderId).toLowerCase() === phKey,
    );
    if (!sourceRow) return doc;

    const winner = slotWinners.get(placeholderTreeSlotKey(sourceRow));
    if (!winner?.StudyPlaceholderId) return doc;
    return { ...doc, StudyPlaceholderId: winner.StudyPlaceholderId };
  });
}

/** One tree row per logical placeholder slot (drops site/study duplicate clones). */
export function dedupeStudyPlaceholdersForTree(phList = [], documents = []) {
  const linkedPhIds = collectLinkedPlaceholderIds(documents, phList);
  const candidates = [];

  (phList || []).forEach((row) => {
    if (String(row?.Status || row?.status || "").toLowerCase() === "waived") {
      return;
    }
    candidates.push(row);
  });

  const bySlot = new Map();
  candidates.forEach((row) => {
    const slotKey = placeholderTreeSlotKey(row);
    const existing = bySlot.get(slotKey);
    if (!existing) {
      bySlot.set(slotKey, row);
      return;
    }
    bySlot.set(
      slotKey,
      placeholderTreeRowScore(row, linkedPhIds) >=
        placeholderTreeRowScore(existing, linkedPhIds)
        ? row
        : existing,
    );
  });

  const deduped = Array.from(bySlot.values());
  const pinned = new Map();

  deduped.forEach((row) => {
    const rowId = stripGuid(row.StudyPlaceholderId).toLowerCase();
    if (linkedPhIds.has(rowId)) {
      pinned.set(rowId, row);
    }
  });

  if (!pinned.size) return deduped;

  const merged = new Map();
  deduped.forEach((row) => {
    const rowId = stripGuid(row.StudyPlaceholderId).toLowerCase();
    if (linkedPhIds.has(rowId) && pinned.has(rowId)) {
      merged.set(`id:${rowId}`, pinned.get(rowId));
      return;
    }
    const slotKey = placeholderTreeSlotKey(row);
    if (!merged.has(`slot:${slotKey}`)) {
      merged.set(`slot:${slotKey}`, row);
    }
  });

  pinned.forEach((row, rowId) => {
    merged.set(`id:${rowId}`, row);
  });

  const finalBySlot = new Map();
  Array.from(merged.values()).forEach((row) => {
    const slotKey = placeholderTreeSlotKey(row);
    const existing = finalBySlot.get(slotKey);
    if (!existing) {
      finalBySlot.set(slotKey, row);
      return;
    }
    finalBySlot.set(
      slotKey,
      placeholderTreeRowScore(row, linkedPhIds) >=
        placeholderTreeRowScore(existing, linkedPhIds)
        ? row
        : existing,
    );
  });

  return Array.from(finalBySlot.values());
}

/** Ensure placeholder rows exist for documents that carry StudyPlaceholderId. */
export function augmentPlaceholdersFromDocuments(phList = [], documents = []) {
  const byId = new Map();
  (phList || []).forEach((row) => {
    const key = stripGuid(row.StudyPlaceholderId).toLowerCase();
    if (key) byId.set(key, row);
  });

  (documents || []).forEach((doc) => {
    const key = stripGuid(
      doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
    ).toLowerCase();
    if (!key || byId.has(key)) return;
    byId.set(key, {
      StudyPlaceholderId: doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
      FolderIdRef: doc.FolderIdRef,
      PlaceholderName: doc.PlaceholderName || doc.FileName || "Expected document",
      Status: "Fulfilled",
      DocumentIdRef: doc.DocumentIdRef,
    });
  });

  return Array.from(byId.values());
}

/** Resolve which study placeholder owns an uploaded document row. */
export function resolveStudyPlaceholderForDocumentRow(
  doc,
  placeholders = [],
  phById = null,
) {
  if (!doc) return null;
  const phList = placeholders || [];
  const index =
    phById ||
    phList.reduce((map, row) => {
      const key = stripGuid(row.StudyPlaceholderId).toLowerCase();
      if (key) map.set(key, row);
      return map;
    }, new Map());

  const docPhKey = stripGuid(
    doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
  ).toLowerCase();
  if (docPhKey && index.has(docPhKey)) return index.get(docPhKey);

  const docId = stripGuid(doc.DocumentIdRef).toLowerCase();
  if (docId) {
    const byStoredDocId = phList.find(
      (p) => stripGuid(p.DocumentIdRef).toLowerCase() === docId,
    );
    if (byStoredDocId) return byStoredDocId;
  }

  return findStudyPlaceholderForDocument(doc, phList);
}

/** Tree node id for a milestone-linked study placeholder (must match mapStudyPlaceholdersToTreeNodes). */
export function getStudyMilestoneTreeNodeId(placeholder) {
  if (!placeholder || !isMilestoneLinkedStudyPlaceholder(placeholder)) return null;

  const folderId = stripGuid(placeholder.FolderIdRef);
  const msId = stripGuid(placeholder.MilestoneId)?.toLowerCase();
  const msCode = String(placeholder.MilestoneCode || placeholder.milestoneCode || "")
    .trim()
    .toUpperCase();
  const bucketKey = msId || `${folderId}:${msCode || "MILESTONE"}`;
  return buildStudyMilestoneTreeNodeId(msId ? placeholder.MilestoneId : null, bucketKey);
}

/** Certification fields returned on FolderDocuments rows (and merged onto tree file nodes). */
export function mapDocumentCertificationFields(item = {}) {
  const certId = item.CertificationId ?? item.certificationId ?? "";
  if (!certId || String(certId).trim() === "") return {};
  return {
    CertificationId: certId,
    CertifiedBy: item.CertifiedBy ?? item.certifiedBy,
    CertifiedByName: item.CertifiedByName ?? item.certifiedByName,
    CertificationDateTime:
      item.CertificationDateTime ?? item.certificationDateTime,
    CertifiedDateTime:
      item.CertifiedDateTime ??
      item.certifiedDateTime ??
      item.CertificationDateTime ??
      item.certificationDateTime,
    DocumentHash: item.DocumentHash ?? item.documentHash,
    CertificationDocumentName:
      item.CertificationDocumentName ?? item.FileName ?? item.fileName,
    SignatureReason: item.SignatureReason ?? item.signatureReason,
    ClientIP: item.ClientIP ?? item.clientIP,
    RegulatoryStandard:
      item.RegulatoryStandard || "21 CFR Part 11 / ICH GCP E6(R3)",
  };
}

/** Active study documents — always nest under placeholder when linked. */
export function mapStudyDocumentsToTreeNodes(documents, placeholders = []) {
  const phList = placeholders || [];
  const phById = new Map();
  phList.forEach((p) => {
    const phId = stripGuid(p.StudyPlaceholderId).toLowerCase();
    if (phId) phById.set(phId, p);
  });
  const parentMap = buildDocumentPlaceholderParentMap(phList, documents);

  return (documents || [])
    .filter((item) => isActiveStudyDocument(item))
    .map((item) => {
      const docKey = stripGuid(item.DocumentIdRef).toLowerCase();
      const docPhKey = stripGuid(
        item.StudyPlaceholderId ?? item.studyPlaceholderId,
      ).toLowerCase();
      const directParentId = docPhKey ? `ph-${docPhKey}` : null;
      const mappedParent = parentMap.get(docKey);
      let ph = resolveStudyPlaceholderForDocumentRow(item, phList, phById);

      if (!ph && docPhKey && !phById.has(docPhKey)) {
        ph =
          phList.find(
            (p) => stripGuid(p.DocumentIdRef).toLowerCase() === docKey,
          ) ||
          phList.find((p) => {
            const fulfilledName = String(
              p.FulfilledDocumentName ?? p.fulfilledDocumentName ?? "",
            )
              .trim()
              .toLowerCase();
            return (
              fulfilledName &&
              fulfilledName === String(item.FileName || "").trim().toLowerCase()
            );
          }) ||
          null;
      }

      if (!ph && mappedParent?.startsWith("ph-")) {
        ph = phById.get(mappedParent.slice(3)) || null;
      }

      const placeholderParentId =
        (ph && getStudyPlaceholderTreeNodeId(ph)) ||
        directParentId ||
        mappedParent ||
        null;
      const folderParentId = stripGuid(item.FolderIdRef);

      return {
        id: item.DocumentIdRef,
        name: normalizeDocumentDisplayName(item.FileName) || "Document",
        type: "file",
        parentId: placeholderParentId || folderParentId,
        folderFallbackId: folderParentId,
        underMilestoneGroup: Boolean(
          ph && isMilestoneLinkedStudyPlaceholder(ph),
        ),
        studyPlaceholderId:
          item.StudyPlaceholderId ??
          item.studyPlaceholderId ??
          ph?.StudyPlaceholderId ??
          null,
        isLocked: item.IsLocked,
        VersionNo: item.VersionNo,
        isMandatorySign:
          item.IsMandatorySign === 1 ||
          item.IsMandatorySign === true ||
          (ph != null && studyPlaceholderRequiresSignature(ph)),
        ...mapDocumentCertificationFields(item),
      };
    });
}

/** Older versions nested under the current (active) document in the tree. */
export function mapPreviousVersionDocumentTreeNodes(
  documents = [],
  activeDocumentNodes = [],
) {
  if (!activeDocumentNodes.length) return [];

  const activeDocIdByGroup = new Map();
  activeDocumentNodes.forEach((node) => {
    const doc = (documents || []).find(
      (d) =>
        stripGuid(d.DocumentIdRef).toLowerCase() ===
        stripGuid(node.id).toLowerCase(),
    );
    const groupKey = stripGuid(doc?.FileGroupIdRef).toLowerCase();
    if (groupKey) activeDocIdByGroup.set(groupKey, stripGuid(node.id));
  });

  return (documents || [])
    .filter((doc) => {
      if (!isSupersededStudyDocument(doc)) return false;
      const groupKey = stripGuid(doc.FileGroupIdRef).toLowerCase();
      return groupKey && activeDocIdByGroup.has(groupKey);
    })
    .map((doc) => {
      const groupKey = stripGuid(doc.FileGroupIdRef).toLowerCase();
      const versionNo = doc.VersionNo;
      const versionPrefix =
        versionNo != null && String(versionNo).trim() !== ""
          ? `V-${versionNo}`
          : "Older version";
      return {
        id: doc.DocumentIdRef,
        name: `${versionPrefix} · ${normalizeDocumentDisplayName(doc.FileName) || "Document"}`,
        type: "file",
        parentId: activeDocIdByGroup.get(groupKey),
        folderFallbackId: stripGuid(doc.FolderIdRef),
        isPreviousVersion: true,
        readOnly: true,
        VersionNo: doc.VersionNo,
        FileGroupIdRef: doc.FileGroupIdRef,
        isLocked: true,
      };
    })
    .sort((a, b) => {
      const va = parseFloat(a.VersionNo) || 0;
      const vb = parseFloat(b.VersionNo) || 0;
      return vb - va;
    });
}

/** Build the study folder tree: folders + placeholders + nested files. */
export function buildStudyDocumentTree(folders = [], placeholders = [], documents = []) {
  const folderIds = new Set(
    (folders || [])
      .map((folder) => normFolderId(folder.id ?? folder.FolderId))
      .filter(Boolean),
  );
  const folderAlignedPlaceholders = alignStudyPlaceholdersToFolders(
    placeholders,
    folders,
  ).filter((row) => {
    const folderId = normFolderId(row?.FolderIdRef);
    // Only nest placeholders that belong to the loaded (site-scoped) folder set.
    return !folderIds.size || (folderId && folderIds.has(folderId));
  });
  const enrichedDocuments = enrichDocumentsWithPlaceholderLinks(
    documents,
    folderAlignedPlaceholders,
  );
  const augmentedPlaceholders = augmentPlaceholdersFromDocuments(
    folderAlignedPlaceholders,
    enrichedDocuments,
  );
  const phList = dedupeStudyPlaceholdersForTree(
    augmentedPlaceholders,
    enrichedDocuments,
  );
  const alignedDocuments = alignDocumentsToTreePlaceholders(
    enrichedDocuments,
    phList,
    augmentedPlaceholders,
  );
  let placeholderNodes = mapStudyPlaceholdersToTreeNodes(phList);
  const placeholderIds = new Set(
    placeholderNodes.map((node) =>
      String(node.id || "")
        .trim()
        .toLowerCase(),
    ),
  );

  alignedDocuments.filter(isActiveStudyDocument).forEach((doc) => {
    const phKey = stripGuid(
      doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
    ).toLowerCase();
    if (!phKey) return;
    const treeId = `ph-${phKey}`;
    if (placeholderIds.has(treeId)) return;

    const phRow =
      phList.find(
        (p) => stripGuid(p.StudyPlaceholderId).toLowerCase() === phKey,
      ) ||
      {
        StudyPlaceholderId: doc.StudyPlaceholderId ?? doc.studyPlaceholderId,
        FolderIdRef: doc.FolderIdRef,
        PlaceholderName: doc.PlaceholderName || doc.FileName || "Expected document",
        Status: "Fulfilled",
        DocumentIdRef: doc.DocumentIdRef,
      };

    const msParent = isMilestoneLinkedStudyPlaceholder(phRow)
      ? getStudyMilestoneTreeNodeId(phRow)
      : null;

    if (msParent && !placeholderIds.has(String(msParent).toLowerCase())) {
      const code = String(phRow.MilestoneCode || "Milestone").trim();
      const name = String(phRow.MilestoneName || code).trim();
      placeholderNodes.push({
        id: msParent,
        name: `${code} — ${name}`,
        type: "milestone",
        parentId: stripGuid(phRow.FolderIdRef),
        milestoneCode: code,
        milestoneName: name,
        milestoneId: phRow.MilestoneId,
        isStudyMilestone: true,
      });
      placeholderIds.add(String(msParent).toLowerCase());
    }

    placeholderNodes.push(
      mapSingleStudyPlaceholderTreeNode(phRow, msParent || undefined),
    );
    placeholderIds.add(treeId);
  });

  const documentNodes = mapStudyDocumentsToTreeNodes(alignedDocuments, phList);
  const previousVersionNodes = mapPreviousVersionDocumentTreeNodes(
    alignedDocuments,
    documentNodes,
  );

  return buildTree([
    ...folders,
    ...placeholderNodes,
    ...documentNodes,
    ...previousVersionNodes,
  ]);
}

function mapSingleStudyPlaceholderTreeNode(p, parentIdOverride) {
  const folderId = stripGuid(p.FolderIdRef);
  const isAdHoc = isAdHocStudyPlaceholder(p);
  const treeId = getStudyPlaceholderTreeNodeId(p);
  return {
    id: treeId || `ph-${p.StudyPlaceholderId}`,
    name: formatStudyPlaceholderTreeLabel(p),
    type: "placeholder",
    parentId: parentIdOverride ?? folderId,
    placeholderStatus: p.Status,
    studyPlaceholderId: p.StudyPlaceholderId,
    dueDate: p.DueDate,
    dueOffsetDays: p.DueOffsetDays,
    dueAnchorType: p.DueAnchorType,
    dueAnchorMilestoneCode: p.DueAnchorMilestoneCode,
    dueAnchorRule: p.DueAnchorRule,
    dueDateIsProvisional: p.DueDateIsProvisional,
    isRequired: p.IsRequired !== false && p.IsRequired !== 0,
    description: p.Description,
    folderName: p.FolderName,
    assignedToName: p.AssignedToName,
    milestoneCode: p.MilestoneCode,
    milestoneName: p.MilestoneName,
    milestoneId: p.MilestoneId,
    IsAdHoc: isAdHoc,
    isAdHoc,
    AssignmentMode: p.AssignmentMode,
    AssigneeRole: p.AssigneeRole,
    AssigneeRoleName: p.AssigneeRoleName,
    DefaultAssigneeRole: p.DefaultAssigneeRole,
    EligibleStaffIds: p.EligibleStaffIds,
    AssignedToStaffId: p.AssignedToStaffId,
    RequiresSignature: p.RequiresSignature,
    SignerRoles: p.SignerRoles,
    underMilestoneGroup: Boolean(parentIdOverride),
    placeholderMeta: {
      Description: p.Description,
      DueDate: p.DueDate,
      MilestoneCode: p.MilestoneCode,
      DueOffsetDays: p.DueOffsetDays,
      DueAnchorType: p.DueAnchorType,
      DueAnchorMilestoneCode: p.DueAnchorMilestoneCode,
      DueAnchorRule: p.DueAnchorRule,
      RequiresSignature: p.RequiresSignature,
      SignerRoles: p.SignerRoles,
    },
  };
}

/**
 * Study tree: milestone-linked placeholders nest under a milestone node;
 * ad-hoc / unlinked placeholders stay directly under the folder.
 */
export function mapStudyPlaceholdersToTreeNodes(placeholders) {
  const list = placeholders || [];
  const standalone = [];
  const milestoneBuckets = new Map();

  list.forEach((p) => {
    if (String(p.Status || "").toLowerCase() === "waived") return;

    if (!isMilestoneLinkedStudyPlaceholder(p)) {
      standalone.push(p);
      return;
    }

    const folderId = stripGuid(p.FolderIdRef);
    const msId = stripGuid(p.MilestoneId)?.toLowerCase();
    const msCode = String(p.MilestoneCode || p.milestoneCode || "")
      .trim()
      .toUpperCase();
    const bucketKey = msId || `${folderId}:${msCode || "MILESTONE"}`;

    if (!milestoneBuckets.has(bucketKey)) {
      milestoneBuckets.set(bucketKey, {
        folderId,
        milestoneId: stripGuid(p.MilestoneId),
        milestoneCode: p.MilestoneCode,
        milestoneName: p.MilestoneName,
        RequiresSignature: p.RequiresSignature,
        SignerRoles: p.SignerRoles,
        dueAnchorRule: p.DueAnchorRule,
        items: [],
      });
    }
    milestoneBuckets.get(bucketKey).items.push(p);
  });

  const milestoneNodes = [];
  const placeholderNodes = [];

  milestoneBuckets.forEach((bucket, bucketKey) => {
    const nodeId = buildStudyMilestoneTreeNodeId(bucket.milestoneId, bucketKey);
    const code = String(bucket.milestoneCode || "Milestone").trim();
    const name = String(bucket.milestoneName || code).trim();

    milestoneNodes.push({
      id: nodeId,
      name: `${code} — ${name}`,
      type: "milestone",
      parentId: bucket.folderId,
      milestoneCode: code,
      milestoneName: name,
      milestoneId: bucket.milestoneId,
      RequiresSignature: bucket.RequiresSignature,
      SignerRoles: bucket.SignerRoles,
      dueAnchorRule: bucket.dueAnchorRule,
      isStudyMilestone: true,
    });

    bucket.items.forEach((p) => {
      if (String(p.Status || "").toLowerCase() === "waived") return;
      placeholderNodes.push(mapSingleStudyPlaceholderTreeNode(p, nodeId));
    });
  });

  standalone.forEach((p) => {
    placeholderNodes.push(mapSingleStudyPlaceholderTreeNode(p));
  });

  return [...milestoneNodes, ...placeholderNodes];
}

export async function saveStudyPlaceholder({
  organizationId,
  studyId,
  folderIdRef,
  templateCategory,
  placeholderName,
  description,
  isRequired,
  dueDate,
  createdBy,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "SaveStudyPlaceholder",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    FolderIdRef: folderIdRef,
    TemplateCategory: templateCategory || null,
    PlaceholderName: placeholderName,
    Description: description || "",
    IsRequired: isRequired !== false,
    DueDate: dueDate || null,
    CreatedBy: createdBy,
  });
  return response?.data?.SuccessStory?.[0] ?? response?.data?.[0] ?? response?.data ?? {};
}

export async function removeStudyPlaceholder({
  organizationId,
  studyId,
  studyPlaceholderId,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "RemoveStudyPlaceholder",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    StudyPlaceholderId: studyPlaceholderId,
    CreatedBy: createdBy,
  });
}

export async function updateStudyPlaceholder({
  organizationId,
  studyId,
  studyPlaceholderId,
  placeholderName,
  description,
  isRequired,
  dueDate,
  assignedToStaffId,
  milestoneCode,
  siteIdRef,
  requiresSignature,
  signerRoles,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "UpdateStudyPlaceholder",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    StudyPlaceholderId: studyPlaceholderId,
    PlaceholderName: placeholderName,
    Description: description,
    IsRequired: isRequired,
    DueDate: dueDate,
    AssignedToStaffId: assignedToStaffId,
    MilestoneCode: milestoneCode || null,
    SiteIdRef: siteIdRef || null,
    RequiresSignature: requiresSignature === true,
    SignerRoles: requiresSignature ? signerRoles || "" : "",
    CreatedBy: createdBy,
  });
}

export const ADHOC_ASSIGNMENT_MODES = [
  {
    value: "PerStaff",
    label: "Each selected person",
    hint: "Creates one placeholder per selected staff member.",
  },
  {
    value: "PerRoleMember",
    label: "Each person in role",
    hint: "Creates one placeholder per staff member with the selected role on this study.",
  },
  {
    value: "SharedRole",
    label: "Any one in role",
    hint: "Creates one shared placeholder — anyone in the role can upload.",
  },
  {
    value: "AnyOneStaff",
    label: "Any one selected person",
    hint: "Creates one shared placeholder — any selected staff member can upload.",
  },
];

export function assignmentModeRequiresStaff(mode) {
  return mode === "PerStaff" || mode === "AnyOneStaff";
}

export function assignmentModeRequiresRole(mode) {
  return mode === "PerRoleMember" || mode === "SharedRole";
}

export function estimateAdHocPlaceholderCount({
  assignmentMode,
  selectedStaffIds = [],
  roleMemberCount = 0,
}) {
  if (assignmentMode === "PerStaff") return selectedStaffIds.length;
  if (assignmentMode === "PerRoleMember") return roleMemberCount;
  if (assignmentMode === "SharedRole" || assignmentMode === "AnyOneStaff") return 1;
  return 0;
}

export function countStudyStaffInRole(staffList = [], roleId, roleOptions = []) {
  if (!roleId) return 0;
  const roleNorm = String(roleId).toLowerCase();
  const roleName = String(
    roleOptions.find((opt) => String(opt.value).toLowerCase() === roleNorm)?.label || "",
  )
    .trim()
    .toLowerCase();
  return (staffList || []).filter((staff) => {
    const staffRole = String(staff?.roleId || staff?.RoleId || "").toLowerCase();
    const staffRoleName = String(staff?.Role || staff?.role || "").toLowerCase();
    return (
      (staffRole && staffRole === roleNorm) ||
      (roleName && staffRoleName === roleName)
    );
  }).length;
}

export function buildAdHocStaffOptions(studyStaffList = [], orgStaffRows = [], siteIdRef = null) {
  const studyIds = new Set(
    (studyStaffList || [])
      .map((staff) =>
        String(staff.value || staff.id || staff.StaffId || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  );

  const siteIds = siteIdRef ? [siteIdRef] : [];

  return (orgStaffRows || [])
    .filter((staff) => {
      const id = String(staff.StaffId || "").trim().toLowerCase();
      if (!id || !studyIds.has(id)) return false;
      if (siteIds.length && !staffMappedToAnySite(staff, siteIds)) return false;
      return true;
    })
    .map((staff) => ({
      staffId: staff.StaffId,
      staffName:
        staff.StaffName ||
        [staff.FirstName, staff.LastName].filter(Boolean).join(" ") ||
        "Unknown",
      roleName: staff.RoleName || staff.Roles || "",
    }))
    .sort((a, b) => a.staffName.localeCompare(b.staffName));
}

export async function saveAdHocStudyPlaceholders({
  organizationId,
  studyId,
  folderIdRef,
  templateCategory,
  siteIdRef,
  milestoneCode,
  placeholderName,
  description,
  isRequired,
  dueDate,
  staffIds = [],
  requiresSignature = false,
  signerRoles = "",
  createdBy,
}) {
  const needsSignature = requiresSignature === true;
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "SaveAdHocStudyPlaceholders",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    FolderIdRef: folderIdRef,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    MilestoneCode: milestoneCode,
    PlaceholderName: placeholderName,
    Description: description || "",
    IsRequired: isRequired !== false,
    DueDate: dueDate || null,
    AssignmentMode: "PerStaff",
    RequiresSignature: needsSignature,
    SignerRoles: needsSignature ? signerRoles || "" : "",
    AdHocStaffIds: staffIds,
    CreatedBy: createdBy,
  });
  return response?.data ?? response;
}

export function computeFolderReadiness(folderCompleteness) {
  const rows = Array.isArray(folderCompleteness) ? folderCompleteness : [];
  if (!rows.length) return { percent: 0, total: 0, complete: 0 };
  const total = rows.length;
  const complete = rows.filter((r) => r.HasDocument === 1 || r.HasDocument === true).length;
  return {
    percent: total ? Math.round((100 * complete) / total) : 0,
    total,
    complete,
  };
}

export function computePlaceholderReadiness(summary) {
  if (!summary) return { percent: 0, mode: "folder" };
  const requiredTotal = Number(summary.RequiredTotal ?? 0);
  if (requiredTotal > 0) {
    return {
      mode: "placeholder",
      percent: Number(summary.ReadinessPercent ?? 0),
      requiredTotal,
      requiredFulfilled: Number(summary.RequiredFulfilled ?? 0),
      overdueCount: Number(summary.OverdueCount ?? 0),
      pendingCount: Number(summary.RequiredPending ?? 0),
    };
  }
  return { mode: "folder", percent: 0 };
}

export async function fetchDashboardPlaceholderStats({
  organizationId,
  studyId,
  templateCategory = null,
  createdBy,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetDashboardPlaceholderStats",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId || null,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
  });
  const raw = extractReportTableRows(response, "PlaceholderStats")[0] ?? {};
  const stats = { ...raw };
  const documentCreationTrend = extractReportTableRows(response, "DocumentCreationTrend");
  const hasDocumentStats =
    stats.Folders !== undefined ||
    stats.Documents !== undefined ||
    stats.folders !== undefined ||
    stats.documents !== undefined;

  return {
    totalPlaceholders: Number(stats.TotalPlaceholders ?? 0),
    requiredTotal: Number(stats.RequiredTotal ?? 0),
    requiredFulfilled: Number(stats.RequiredFulfilled ?? 0),
    pendingRequired: Number(stats.PendingRequired ?? stats.PendingUpload ?? 0),
    pendingUpload: Number(stats.PendingUpload ?? stats.PendingRequired ?? 0),
    pendingSign: Number(stats.PendingSign ?? 0),
    overdueCount: Number(stats.OverdueCount ?? 0),
    readinessPercent: Number(stats.ReadinessPercent ?? 0),
    hasDocumentStats,
    folders: Number(stats.Folders ?? 0),
    documents: Number(stats.Documents ?? 0),
    lockedDocuments: Number(stats.LockedDocuments ?? 0),
    signedDocuments: Number(stats.SignedDocuments ?? 0),
    memoryUsed: Number(stats.MemoryUsed ?? 0),
    documentCreationTrend,
  };
}
